import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import Chart from 'react-apexcharts';
import Select from 'react-select';
import { reactSelectStyles } from '../utils/reactSelectStyles';
import { initiativesApi, reportsApi, metricsApi, readingsApi } from '../services/trackerApi';
import { Sparkline, MetricRing } from './CatalogPage';
import { peopleApi, usersApi, meetingMinutesApi, linkedArtefactsApi, assetsApi } from '../services/api';
import { totalAnnualSpendUsd, formatUsd } from '../utils/assetSpend';
import { isRichTextEmpty } from '../components/RichTextField';
import { RichTextView } from '../components/RichTextView';
import { DocumentManager } from '../components/DocumentManager';
import { LinkedArtefacts } from '../components/LinkedArtefacts';
import { entryPlainText, todayIso } from './minutes/MinuteEntryRow';
import { describeApiError } from '../utils/apiError';
import { useToast } from '../hooks/useToast';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { formatDate, fmtRelative, HEALTH_COLOR, STATUS_COLOR, PRIORITY_COLOR, TIER_ICON, tierSlug, effectiveHealth, initials, milestoneCompletion, milestoneProgress, ciHealth, operationalHealth, scoreTone, computeMetricStatus } from '../utils/format';
import { buildCensus, censusCategoriesForSubtree, formatMilestoneChange } from '../utils/reportRollup';
import { PageHeader } from '../components/PageHeader';
import { NimbusTable } from '../components/NimbusTable';
import { SEVERITY_TONE } from '../constants/tone';
import type { Initiative, Risk, Action, Decision, WeeklyReport, HistoryEntry, ProjectedEndDateException } from '../types/trackerModels';
import type { MeetingMinutes, MinuteEntry } from '../types/models';

const TABS = ['Overview', 'Reports', 'Minutes', 'Actions', 'Decisions', 'Risks & Blockers', 'Metrics', 'Timeline', 'Progress', 'Exceptions', 'Linked Artefacts'] as const;

// np-tone-* uses "neutral" where the old Bootstrap colour name was "secondary".
const toneOf = (color: string): string => (color === 'secondary' ? 'neutral' : color);

const REQ_STATUS_OPTIONS = ['Proposed', 'Approved', 'In Progress', 'Done'];
const REQ_STATUS_ICON: Record<string, string> = {
  Proposed: 'ri-checkbox-blank-circle-line',
  Approved: 'ri-checkbox-circle-line',
  'In Progress': 'ri-time-line',
  Done: 'ri-checkbox-circle-fill',
};
const REQ_STATUS_COLOR: Record<string, string> = {
  Proposed: 'var(--bs-secondary)',
  Approved: 'var(--bs-info)',
  'In Progress': 'var(--bs-warning)',
  Done: 'var(--bs-success)',
};

/**
 * The trailing "History" column shared by Requirements, Scope and Milestones — one
 * definition so the three cards can't drift into showing this differently (the same
 * reasoning the ApprovalStamp it replaces was built on). A single chip per row:
 *
 *  - "Approved" (green) if currently approved, "{N} changes" if there's a log but it isn't
 *    currently approved, "Approve" if nothing has happened yet but an approve action is
 *    available, or a plain "—" with no trigger at all if neither applies (a requirement
 *    with no history — its status, and so its approval, only changes via the dropdown
 *    elsewhere in its row, not from here).
 *  - Click opens a popover: the Approve/Unapprove action first (only present when the
 *    caller passes onApprove/onUnapprove — omitted for requirements, whose approval IS the
 *    status dropdown, not a separate action), then every HistoryEntry newest-first.
 */
const HistoryPopoverTrigger: React.FC<{
  approvedBy?: string | null;
  history?: HistoryEntry[];
  approverName: (personId: string) => string;
  onApprove?: () => void;
  onUnapprove?: () => void;
  busy?: boolean;
}> = ({ approvedBy, history = [], approverName, onApprove, onUnapprove, busy }) => {
  const [open, setOpen] = useState(false);
  const hasLog = history.length > 0;
  const canToggle = !!(onApprove || onUnapprove);

  if (!hasLog && !canToggle) {
    return <span className="np-sub" style={{ fontSize: 11 }}>—</span>;
  }

  const label = approvedBy ? 'Approved' : hasLog ? `${history.length} change${history.length === 1 ? '' : 's'}` : 'Approve';

  return (
    <div className="position-relative d-inline-block">
      <button type="button" className="np-btn np-btn-sm p-0 border-0 bg-transparent" onClick={() => setOpen((o) => !o)}>
        <span className={`np-chip np-tone-${approvedBy ? 'success' : 'neutral'}`} style={{ cursor: 'pointer' }}>
          {approvedBy && <i className="ri-shield-check-line"></i>}
          {label}
        </span>
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 1040 }} onClick={() => setOpen(false)} />
          <div className="np-popover" style={{ top: '100%', right: 0, left: 'auto', marginTop: '0.375rem', minWidth: '15rem', zIndex: 1050 }}>
            <div className="np-popover-head"><span>History</span></div>
            {canToggle && (
              <div className="px-2 pb-2">
                {approvedBy ? (
                  <button
                    type="button"
                    className="np-btn np-btn-sm np-btn-ghost w-100"
                    disabled={busy}
                    onClick={() => { onUnapprove?.(); setOpen(false); }}
                  >
                    Unapprove
                  </button>
                ) : (
                  <button
                    type="button"
                    className="np-btn np-btn-sm np-btn-primary w-100"
                    disabled={busy}
                    onClick={() => { onApprove?.(); setOpen(false); }}
                  >
                    Approve
                  </button>
                )}
              </div>
            )}
            {hasLog ? (
              <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                {[...history].reverse().map((h) => (
                  <div key={h.id} className="px-2 py-2" style={{ fontSize: 12, borderTop: '1px solid var(--np-border)' }}>
                    <div>{h.action}</div>
                    <div className="np-sub" style={{ fontSize: 11 }}>{approverName(h.by)} · {fmtRelative(h.at)}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="np-sub px-2 pb-2" style={{ fontSize: 12 }}><i className="ri-history-line me-1"></i>No changes recorded yet.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/** One row of the Scope table — a scope item has no progress to track, it is either in or
 *  out, so there is no status column, only Item and the trailing History/approve column;
 *  the row's own text is still edited on the initiative form. */
const ScopeRow: React.FC<{
  item: { id: string; text: string; status: string; approvedBy?: string | null; approvedAt?: string | null; history?: HistoryEntry[] };
  inScope: boolean;
  approverName: (personId: string) => string;
  onApprove: (itemId: string) => void;
  onUnapprove: (itemId: string) => void;
  busy: boolean;
}> = ({ item, inScope, approverName, onApprove, onUnapprove, busy }) => (
  <tr>
    <td style={{ width: '110px' }}>
      <span className={`np-chip np-tone-${inScope ? 'success' : 'neutral'}`}>
        <i className={inScope ? 'ri-checkbox-circle-line' : 'ri-indeterminate-circle-line'}></i>
        {inScope ? 'In' : 'Out'}
      </span>
    </td>
    <td>{item.text}</td>
    <td style={{ width: '130px' }}>
      <HistoryPopoverTrigger
        approvedBy={item.approvedBy}
        history={item.history}
        approverName={approverName}
        onApprove={() => onApprove(item.id)}
        onUnapprove={() => onUnapprove(item.id)}
        busy={busy}
      />
    </td>
  </tr>
);

/** One row of a Requirements table. Status still changes only via the dropdown here — the
 *  History column is a read-only log viewer for requirements, since there's no separate
 *  approve action to offer (unlike Scope/Milestones, which have nothing else to click). */
const RequirementRow: React.FC<{
  req: { id: string; text: string; status: string; approvedBy?: string | null; approvedAt?: string | null; history?: HistoryEntry[] };
  kind: string;
  onStatusChange: (status: string) => void;
  approverName: (personId: string) => string;
}> = ({ req, onStatusChange, approverName }) => {
  const [editing, setEditing] = useState(false);
  const color = REQ_STATUS_COLOR[req.status] || 'var(--bs-secondary)';
  return (
    <tr>
      <td>{req.text}</td>
      <td style={{ width: '150px' }}>
        {editing ? (
          <select
            className="np-select"
            value={req.status}
            autoFocus
            onBlur={() => setEditing(false)}
            onChange={(e) => { onStatusChange(e.target.value); setEditing(false); }}
          >
            {REQ_STATUS_OPTIONS.map((s) => <option key={s}>{s}</option>)}
          </select>
        ) : (
          <button className="np-btn np-btn-sm np-btn-ghost p-0 d-flex align-items-center gap-1" style={{ fontSize: 11.5, color }} onClick={() => setEditing(true)}>
            <i className={REQ_STATUS_ICON[req.status] || 'ri-checkbox-blank-circle-line'}></i>
            {req.status}
          </button>
        )}
      </td>
      <td style={{ width: '130px' }}>
        <HistoryPopoverTrigger approvedBy={req.approvedBy} history={req.history} approverName={approverName} />
      </td>
    </tr>
  );
};

export const InitiativeDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<(typeof TABS)[number]>('Overview');
  const [showComplete, setShowComplete] = useState(false);
  const [showNewMetricForm, setShowNewMetricForm] = useState(false);
  const [newMetricForm, setNewMetricForm] = useState({ name: '', unit: '', direction: 'higher', cadence: 'Monthly', target: '', baseline: '', criticality: 'Medium' });
  const [sendingToCatalog, setSendingToCatalog] = useState<string | null>(null);
  const [sendForm, setSendForm] = useState({ bauOwnerId: '', cadence: 'Monthly' });
  const [newRisk, setNewRisk] = useState({ description: '', severity: 'Medium', status: 'Open' });
  const [confirmDeleteInitiative, setConfirmDeleteInitiative] = useState(false);
  const [confirmDeleteReport, setConfirmDeleteReport] = useState<string | null>(null);
  // Which direct child's own children (Portfolio drill-down only) are currently expanded to
  // show Project-level detail underneath a Programme row - one at a time, fetched on demand
  // rather than eagerly fanning out every Programme's own children up front.
  const [expandedChildId, setExpandedChildId] = useState<string | null>(null);
  // "What changed this week" census - collapsed to the top few rows per category by default,
  // same overview-not-detail intent as SubmitReportPage.tsx's own showAllRequirements toggle,
  // since a Portfolio's census can easily reach a dozen+ rows once every real percent move
  // counts (see reportRollup.ts's diffMilestones).
  const [showAllMilestoneChanges, setShowAllMilestoneChanges] = useState(false);
  const [showAllRequirementChanges, setShowAllRequirementChanges] = useState(false);
  const CENSUS_ROW_CAP = 5;
  const [editingRisk, setEditingRisk] = useState<string | null>(null);
  const [riskForm, setRiskForm] = useState<Risk | null>(null);
  const [confirmDeleteRisk, setConfirmDeleteRisk] = useState<string | null>(null);
  const [newAction, setNewAction] = useState({ description: '', ownerId: '', dueDate: '', status: 'Open' as Action['status'] });
  const [editingAction, setEditingAction] = useState<string | null>(null);
  const [actionForm, setActionForm] = useState<Action | null>(null);
  const [confirmDeleteAction, setConfirmDeleteAction] = useState<string | null>(null);
  const [newDecision, setNewDecision] = useState({ description: '', decidedBy: '' });
  const [editingDecision, setEditingDecision] = useState<string | null>(null);
  const [decisionForm, setDecisionForm] = useState<Decision | null>(null);
  const [confirmDeleteDecision, setConfirmDeleteDecision] = useState<string | null>(null);

  const { data: initiative } = useQuery<Initiative>({
    queryKey: ['initiative', id],
    queryFn: () => initiativesApi.getOne(id!),
    enabled: !!id,
  });
  const { data: people = [] } = useQuery({ queryKey: ['people'], queryFn: peopleApi.getAll });
  // Same cache key AllInitiativesPage/OverviewPage already populate, just to resolve the
  // parent's name and count children for the chip row below - not refetched on every visit.
  const { data: allInitiatives = [] } = useQuery<Initiative[]>({ queryKey: ['initiatives'], queryFn: initiativesApi.getAll });
  const parentInitiative = initiative?.parentInitiativeId
    ? allInitiatives.find((i) => i.initiativeId === initiative.parentInitiativeId)
    : undefined;
  // Memoized (not a plain conditional) because several rollup memos below depend on it - an
  // unmemoized array here would get a fresh reference every render, silently defeating their
  // own memoization.
  const childInitiatives = useMemo(
    () => (initiative ? allInitiatives.filter((i) => i.parentInitiativeId === initiative.initiativeId) : []),
    [initiative, allInitiatives],
  );
  // Every descendant at any depth (Portfolio -> Programmes -> Projects), not just direct
  // children - unlike CI Health/the report census (deliberately one hop, since those are about
  // discrete deliverables and diffs), Operational Health is a flat measurement rollup: the
  // metrics that actually matter operationally mostly live at Project tier, two hops below a
  // Portfolio, and a Portfolio whose own targetMetricIds is thin (often just one spend metric,
  // frequently with no target at all) shouldn't show "not enough data" while 4 real Project-level
  // metrics with real targets and readings sit right underneath it unrepresented. No extra fetch
  // needed - allInitiatives is already the whole tenant, fetched above.
  const descendantInitiatives = useMemo(() => {
    if (!initiative) return [];
    const byParent = new Map<string, Initiative[]>();
    allInitiatives.forEach((i) => {
      if (!i.parentInitiativeId) return;
      const list = byParent.get(i.parentInitiativeId) || [];
      list.push(i);
      byParent.set(i.parentInitiativeId, list);
    });
    const result: Initiative[] = [];
    const queue = [initiative.initiativeId];
    while (queue.length) {
      const kids = byParent.get(queue.shift()!) || [];
      kids.forEach((k) => { result.push(k); queue.push(k.initiativeId); });
    }
    return result;
  }, [initiative, allInitiatives]);

  // Meetings pinned to this initiative - still what the Minutes tab's own document list and
  // the carry-forward panel key off. Enabled only once we have an id, so the /initiatives/new
  // case doesn't fire a query for an empty initiativeId.
  const { data: pinnedMinutes = [], isLoading: minutesLoading } = useQuery({
    queryKey: ['meetingMinutes', 'initiative', id],
    queryFn: () => meetingMinutesApi.list(id!),
    enabled: !!id,
  });
  // Every meeting, tenant-wide - entries can now be linked to an initiative independently of
  // which one (if any) their own meeting is pinned to, so surfacing everything this initiative
  // cares about means looking past just its pinned meetings.
  const { data: allMinutes = [] } = useQuery({
    queryKey: ['meetingMinutes', 'all'],
    queryFn: () => meetingMinutesApi.list(),
  });
  // A Programme rolls up Actions/Risks & Blockers/Decisions/Minutes/Exceptions from its direct
  // child Projects (not transitively past them, same one-level convention ciHealth already
  // uses for childInitiatives) - Portfolios don't get this, only Programmes, per how it was
  // asked for. rollupIds is just "this initiative, plus its children when it's a Programme" -
  // every rollup below keys off this one set.
  const rollupIds = useMemo<string[]>(() => {
    if (!id) return [];
    if (initiative?.tier !== 'Program') return [id];
    return [id, ...childInitiatives.map((c) => c.initiativeId)];
  }, [id, initiative?.tier, childInitiatives]);
  const rollupSourceName = (initiativeId: string): string | null => {
    if (initiativeId === id) return null;
    return childInitiatives.find((c) => c.initiativeId === initiativeId)?.name || null;
  };
  // One row per entry relevant to this initiative (or, for a Programme, relevant to it or any
  // direct child), deduped, from either linkage: the entry's own initiativeIds (new, and the
  // only way an entry from a meeting pinned elsewhere - or nowhere - can still surface here), or
  // its meeting being pinned to one of rollupIds (the original, coarser relationship - kept so
  // entries predating per-entry linking still show up where they always did). matchedInitiativeId
  // records which member of rollupIds it matched, so a rolled-up row can be tagged with its
  // source project.
  const linkedEntries = useMemo<{ source: MeetingMinutes; entry: MinuteEntry; matchedInitiativeId: string }[]>(() => {
    if (!id) return [];
    const seen = new Set<string>();
    const items: { source: MeetingMinutes; entry: MinuteEntry; matchedInitiativeId: string }[] = [];
    allMinutes.forEach((m) => {
      (m.entries || []).forEach((e) => {
        const matched = rollupIds.find((rid) => (e.initiativeIds || []).includes(rid) || m.initiativeId === rid);
        if (!matched || seen.has(e.entryId)) return;
        seen.add(e.entryId);
        items.push({ source: m, entry: e, matchedInitiativeId: matched });
      });
    });
    return items;
  }, [allMinutes, id, rollupIds]);
  const byMeetingDateDesc = (a: { source: MeetingMinutes }, b: { source: MeetingMinutes }) =>
    (b.source.meetingDate || '').localeCompare(a.source.meetingDate || '');
  // Overdue ones surfaced ahead of everything else, then newest meeting first — the two things
  // a reader actually scans for.
  const openActions = useMemo<{ source: MeetingMinutes; entry: MinuteEntry; matchedInitiativeId: string }[]>(() => {
    const items = linkedEntries.filter(({ entry }) => entry.category === 'action' && entry.status === 'open');
    const today = todayIso();
    return items.sort((a, b) => {
      const aOverdue = !!a.entry.dueDate && a.entry.dueDate < today;
      const bOverdue = !!b.entry.dueDate && b.entry.dueDate < today;
      if (aOverdue !== bOverdue) return aOverdue ? -1 : 1;
      return byMeetingDateDesc(a, b);
    });
  }, [linkedEntries]);
  const decisionEntries = useMemo<{ source: MeetingMinutes; entry: MinuteEntry; matchedInitiativeId: string }[]>(
    () => linkedEntries.filter(({ entry }) => entry.category === 'decision').sort(byMeetingDateDesc),
    [linkedEntries],
  );
  const riskBlockerEntries = useMemo<{ source: MeetingMinutes; entry: MinuteEntry; matchedInitiativeId: string }[]>(
    () => linkedEntries.filter(({ entry }) => entry.category === 'risk' || entry.category === 'blocker').sort(byMeetingDateDesc),
    [linkedEntries],
  );
  // Minutes tab rollup: every meeting pinned to this initiative, plus (for a Programme) every
  // meeting pinned directly to one of its child Projects. Unlike the entries above, a meeting
  // itself isn't multi-linkable - m.initiativeId is a single pin - so this is a plain id-set
  // filter over the already-fetched allMinutes, merged with the existing own-initiative query
  // rather than replacing it (keeps the non-Programme case byte-for-byte identical to before).
  const pinnedMinutesRollup = useMemo<MeetingMinutes[]>(() => {
    if (initiative?.tier !== 'Program' || childInitiatives.length === 0) return pinnedMinutes;
    const childIds = new Set(childInitiatives.map((c) => c.initiativeId));
    const byId = new Map(pinnedMinutes.map((m) => [m.minutesId, m]));
    allMinutes.forEach((m) => { if (childIds.has(m.initiativeId)) byId.set(m.minutesId, m); });
    return Array.from(byId.values()).sort((a, b) => (b.meetingDate || '').localeCompare(a.meetingDate || ''));
  }, [pinnedMinutes, allMinutes, initiative?.tier, childInitiatives]);
  // Scoped to just the meetings actually listed on the Minutes tab below (including, for a
  // Programme, its children's), unlike openActions further up which also picks up entries
  // linked here from meetings pinned elsewhere.
  const pinnedOpenActionCount = pinnedMinutesRollup.reduce(
    (n, m) => n + (m.entries || []).filter((e) => e.category === 'action' && e.status === 'open').length,
    0,
  );
  // Risks/Exceptions live as embedded arrays on the initiative document itself (not derived from
  // a tenant-wide list like the minute entries above), so rolling them up for a Programme means
  // reading each child's own already-fetched record straight out of allInitiatives - no extra
  // request needed. Read-only here: editing one means writing to the CHILD's own record, which
  // is a different mutation target than this page's addRiskMutation/approveExceptionMutation
  // (both hard-coded to `id!`) - so these rolled-up rows link out to the child's own tab instead.
  const rolledUpRisks = useMemo<{ risk: Risk; fromInitiative: Initiative }[]>(() => {
    if (initiative?.tier !== 'Program') return [];
    return childInitiatives.flatMap((c) => (c.risks || []).map((risk) => ({ risk, fromInitiative: c })));
  }, [initiative?.tier, childInitiatives]);
  const rolledUpExceptions = useMemo<{ exception: ProjectedEndDateException; fromInitiative: Initiative }[]>(() => {
    if (initiative?.tier !== 'Program') return [];
    return childInitiatives.flatMap((c) => (c.exceptions || []).map((exception) => ({ exception, fromInitiative: c })));
  }, [initiative?.tier, childInitiatives]);
  // Actions/decisions added directly on an initiative are the same shape as risks/exceptions
  // above (an embedded array on the initiative record, not a tenant-wide list) - same rollup,
  // same read-only-and-link-out treatment for the same reason.
  const rolledUpActions = useMemo<{ action: Action; fromInitiative: Initiative }[]>(() => {
    if (initiative?.tier !== 'Program') return [];
    return childInitiatives.flatMap((c) => (c.actions || []).map((action) => ({ action, fromInitiative: c })));
  }, [initiative?.tier, childInitiatives]);
  const rolledUpDecisions = useMemo<{ decision: Decision; fromInitiative: Initiative }[]>(() => {
    if (initiative?.tier !== 'Program') return [];
    return childInitiatives.flatMap((c) => (c.decisions || []).map((decision) => ({ decision, fromInitiative: c })));
  }, [initiative?.tier, childInitiatives]);
  const { data: reports = [] } = useQuery<WeeklyReport[]>({
    queryKey: ['reports', id],
    queryFn: () => reportsApi.list(id!),
    enabled: !!id,
  });
  // Program/Portfolio Reports tab dashboard: each direct child's own latest report (and the
  // one before it, for a delta) - only ever rendered for those two tiers, but the fan-out
  // itself has to run unconditionally like every other hook on this page.
  const childReportQueries = useQueries({
    queries: childInitiatives.map((c: any) => ({
      queryKey: ['reports', c.initiativeId],
      queryFn: () => reportsApi.list(c.initiativeId),
      enabled: !!c.initiativeId,
    })),
  });
  const childReports: Record<string, { latest?: WeeklyReport; delta: number | null }> = {};
  const latestReportByChild: Record<string, WeeklyReport | undefined> = {};
  const previousReportByChild: Record<string, WeeklyReport | undefined> = {};
  childInitiatives.forEach((c: any, i: number) => {
    const sorted = ((childReportQueries[i]?.data as WeeklyReport[]) || []).slice().sort((a, b) => b.weekOf.localeCompare(a.weekOf));
    const [latest, previous] = sorted;
    childReports[c.initiativeId] = { latest, delta: latest && previous ? latest.percentComplete - previous.percentComplete : null };
    latestReportByChild[c.initiativeId] = latest;
    previousReportByChild[c.initiativeId] = previous;
  });
  // Census source: the nearest tier that actually owns milestones/requirements. For Program,
  // that's its direct children (already Project tier) - childReportQueries above already covers
  // it. For Portfolio, direct children are Programmes, which never own milestones/requirements
  // themselves (only Projects do - a Programme's own WeeklyReport always submits both as empty),
  // so diffing direct children would always show zero changes no matter what actually happened
  // underneath. Confirmed live 2026-09-22: a real Portfolio with real milestone/requirement
  // activity across its Projects still showed "0 changes", because the census was only ever
  // diffing its Programmes' own permanently-empty milestoneUpdates/requirementUpdates. Reaching
  // Project-tier descendants directly (via descendantInitiatives, already fetched for Operational
  // Health) fixes this without needing every Programme to somehow re-publish its own children's
  // diffs as new structured fields - it's a second, independent fan-out from childReportQueries,
  // but same query keys, so React Query dedupes it to zero extra network calls for Program tier
  // (where the two sets are identical) and for any Programme also visited via the drill-down.
  const censusSourceInitiatives = initiative?.tier === 'Portfolio'
    ? descendantInitiatives.filter((d) => d.tier === 'Project')
    : childInitiatives;
  const censusReportQueries = useQueries({
    queries: censusSourceInitiatives.map((c: any) => ({
      queryKey: ['reports', c.initiativeId],
      queryFn: () => reportsApi.list(c.initiativeId),
      enabled: !!c.initiativeId,
    })),
  });
  const censusLatestByChild: Record<string, WeeklyReport | undefined> = {};
  const censusPreviousByChild: Record<string, WeeklyReport | undefined> = {};
  censusSourceInitiatives.forEach((c: any, i: number) => {
    const sorted = ((censusReportQueries[i]?.data as WeeklyReport[]) || []).slice().sort((a, b) => b.weekOf.localeCompare(a.weekOf));
    censusLatestByChild[c.initiativeId] = sorted[0];
    censusPreviousByChild[c.initiativeId] = sorted[1];
  });
  // Cheap to compute unconditionally even for Project tier (buildCensus([]) is a no-op) rather
  // than gating it, same convention as every other rollup memo on this page.
  const reportCensus = buildCensus(censusSourceInitiatives, censusLatestByChild, censusPreviousByChild);
  // Portfolio drill-down: only the currently-expanded Programme's own children get fetched,
  // straight from the already-fully-fetched allInitiatives (no new tenant-wide query) plus a
  // small fan-out for just their reports.
  const expandedGrandchildren = useMemo(
    () => (expandedChildId ? allInitiatives.filter((i) => i.parentInitiativeId === expandedChildId) : []),
    [allInitiatives, expandedChildId],
  );
  const grandchildReportQueries = useQueries({
    queries: expandedGrandchildren.map((c) => ({
      queryKey: ['reports', c.initiativeId],
      queryFn: () => reportsApi.list(c.initiativeId),
      enabled: !!c.initiativeId,
    })),
  });
  const grandchildLatestReport: Record<string, WeeklyReport | undefined> = {};
  expandedGrandchildren.forEach((c, i) => {
    const sorted = ((grandchildReportQueries[i]?.data as WeeklyReport[]) || []).slice().sort((a, b) => b.weekOf.localeCompare(a.weekOf));
    grandchildLatestReport[c.initiativeId] = sorted[0];
  });
  const { data: metrics = [] } = useQuery({ queryKey: ['metrics'], queryFn: metricsApi.getAll });
  const targetMetrics = metrics.filter((m: any) => initiative?.targetMetricIds?.includes(m.metricId));
  // Operational Health's own input: this initiative's metrics UNIONED with every descendant's
  // (see descendantInitiatives above) - deduped, since a metric could in principle be targeted
  // by more than one initiative. targetMetrics itself stays own-only, unchanged, for the Metrics
  // tab and the "hand off to BAU" flow, which are both about what THIS initiative directly owns.
  const rollupMetrics = useMemo(() => {
    const ownIds = new Set<string>(initiative?.targetMetricIds || []);
    const descendantIds = new Set<string>();
    descendantInitiatives.forEach((d) => (d.targetMetricIds || []).forEach((id) => descendantIds.add(id)));
    return metrics.filter((m: any) => {
      if (ownIds.has(m.metricId)) return true; // unfiltered, same as targetMetrics always was
      // A descendant's metric only counts once it's graduated to an ongoing, BAU-tracked
      // signal - the same maturity gate "Send to Catalog" already uses to decide whether a
      // Project's metric is worth linking into its parent's own targetMetricIds. Without this,
      // every raw, unproven Project-delivery metric would flood a Portfolio's health score the
      // moment it exists, which is exactly what that gate exists to prevent - this just applies
      // it automatically at any depth instead of requiring a manual re-link at every level up.
      return descendantIds.has(m.metricId) && m.trackedAsBau === true;
    });
  }, [initiative, descendantInitiatives, metrics]);
  // Reading history for the rollup set (superset of targetMetrics) - scoped to just this
  // initiative's own subtree's metrics (still a small set for any one initiative), unlike
  // CatalogPage.tsx's tenant-wide version of this same fan-out, which fetches readings for every
  // metric in the catalog. Must run unconditionally, above the `!initiative` early return below -
  // React Hooks can't be called conditionally or after an early return, which is exactly why
  // targetMetrics/rollupMetrics above use initiative?. rather than assuming it's loaded yet.
  const readingQueries = useQueries({
    queries: rollupMetrics.map((m: any) => ({
      queryKey: ['readings', m.metricId],
      queryFn: () => readingsApi.list(m.metricId),
      enabled: !!m.metricId,
    })),
  });
  const readingsByMetric: Record<string, { value: number }[]> = {};
  rollupMetrics.forEach((m: any, i: number) => {
    readingsByMetric[m.metricId] = readingQueries[i]?.data || [];
  });
  // Total Annual Spend rollup: every asset this initiative has linked via Linked Artefacts
  // (any tier - Project/Programme/Portfolio all use the same tab). The link record itself only
  // carries display fields (title/accountable), not billingEntries, so each linked asset needs
  // its own follow-up fetch - a second-level fan-out under the links query, same shape as
  // readingQueries above.
  const { data: linkedArtefactsData } = useQuery({
    queryKey: ['linkedArtefacts', id, 'initiative'],
    queryFn: () => linkedArtefactsApi.getLinks(id!, 'initiative'),
    enabled: !!id,
  });
  const linkedAssetIds = ((linkedArtefactsData as any)?.links || [])
    .filter((l: any) => l.artefact_type === 'asset')
    .map((l: any) => l.artefact_id);
  const linkedAssetQueries = useQueries({
    queries: linkedAssetIds.map((assetId: string) => ({
      queryKey: ['asset', assetId],
      queryFn: () => assetsApi.getOne(assetId),
      enabled: !!assetId,
    })),
  });
  const linkedAssets = linkedAssetQueries.map((q) => q.data).filter(Boolean) as any[];
  const linkedAssetsLoaded = linkedAssetIds.length === 0 || linkedAssetQueries.every((q) => q.isFetched);
  const totalAnnualSpendRollup = linkedAssets.reduce((sum, a) => sum + totalAnnualSpendUsd(a.billingEntries || []), 0);

  // Approval gate is tied to real platform user accounts (Team Management's "Exception
  // Approver" toggle), not the tracker's own People register — signing off on a schedule
  // exception is a platform-access permission, not a data-attribution concern.
  const { data: currentUser } = useQuery({ queryKey: ['currentUser'], queryFn: usersApi.me });
  const { personId: currentPersonId } = useCurrentUser();
  const isApprover = !!currentUser?.isExceptionApprover;

  const addRiskMutation = useMutation({
    mutationFn: () =>
      initiativesApi.update(id!, {
        risks: [...(initiative?.risks || []), { id: `RSK-${Date.now()}`, ...newRisk }],
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['initiative', id] });
      setNewRisk({ description: '', severity: 'Medium', status: 'Open' });
      toast.success('Risk added.');
    },
  });

  const updateRiskMutation = useMutation({
    mutationFn: (updatedRisk: Risk) =>
      initiativesApi.update(id!, { risks: (initiative?.risks || []).map((r) => (r.id === updatedRisk.id ? updatedRisk : r)) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['initiative', id] });
      setEditingRisk(null);
      setRiskForm(null);
      toast.success('Risk updated.');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to update risk'),
  });

  const deleteRiskMutation = useMutation({
    mutationFn: (riskId: string) =>
      initiativesApi.update(id!, { risks: (initiative?.risks || []).filter((r) => r.id !== riskId) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['initiative', id] });
      setConfirmDeleteRisk(null);
      toast.success('Risk removed.');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to remove risk'),
  });

  const addActionMutation = useMutation({
    mutationFn: () =>
      initiativesApi.update(id!, {
        actions: [...(initiative?.actions || []), { id: `ACT-${Date.now()}`, ...newAction }],
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['initiative', id] });
      setNewAction({ description: '', ownerId: '', dueDate: '', status: 'Open' });
      toast.success('Action added.');
    },
  });

  const updateActionMutation = useMutation({
    mutationFn: (updatedAction: Action) =>
      initiativesApi.update(id!, { actions: (initiative?.actions || []).map((a) => (a.id === updatedAction.id ? updatedAction : a)) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['initiative', id] });
      setEditingAction(null);
      setActionForm(null);
      toast.success('Action updated.');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to update action'),
  });

  const deleteActionMutation = useMutation({
    mutationFn: (actionId: string) =>
      initiativesApi.update(id!, { actions: (initiative?.actions || []).filter((a) => a.id !== actionId) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['initiative', id] });
      setConfirmDeleteAction(null);
      toast.success('Action removed.');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to remove action'),
  });

  const addDecisionMutation = useMutation({
    mutationFn: () =>
      initiativesApi.update(id!, {
        decisions: [...(initiative?.decisions || []), { id: `DEC-${Date.now()}`, ...newDecision }],
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['initiative', id] });
      setNewDecision({ description: '', decidedBy: '' });
      toast.success('Decision recorded.');
    },
  });

  const updateDecisionMutation = useMutation({
    mutationFn: (updatedDecision: Decision) =>
      initiativesApi.update(id!, { decisions: (initiative?.decisions || []).map((d) => (d.id === updatedDecision.id ? updatedDecision : d)) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['initiative', id] });
      setEditingDecision(null);
      setDecisionForm(null);
      toast.success('Decision updated.');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to update decision'),
  });

  const deleteDecisionMutation = useMutation({
    mutationFn: (decisionId: string) =>
      initiativesApi.update(id!, { decisions: (initiative?.decisions || []).filter((d) => d.id !== decisionId) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['initiative', id] });
      setConfirmDeleteDecision(null);
      toast.success('Decision removed.');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to remove decision'),
  });

  const closeActionMutation = useMutation({
    mutationFn: ({ source, entry }: { source: MeetingMinutes; entry: MinuteEntry }) =>
      meetingMinutesApi.closeAction(source.minutesId, entry.entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meetingMinutes'] });
      toast.success('Action closed on the minutes that raised it.');
    },
    onError: (err) => toast.error(describeApiError(err, 'Could not close that action.')),
  });

  /** Appends one entry to an item's history array — every caller below wants "keep what's
   *  there, add this on the end", so it's factored out once rather than repeated per mutation. */
  const appendHistory = (history: HistoryEntry[] | undefined, action: string): HistoryEntry[] => [
    ...(history || []),
    { id: crypto.randomUUID(), at: new Date().toISOString(), by: currentPersonId, action },
  ];

  const updateRequirementStatus = useMutation({
    mutationFn: (args: { kind: 'functionalRequirements' | 'nonFunctionalRequirements'; reqId: string; status: string }) =>
      initiativesApi.update(id!, {
        [args.kind]: (initiative?.[args.kind] || []).map((r) => (r.id === args.reqId ? {
          ...r,
          status: args.status,
          // approvedBy/approvedAt track whether the CURRENT status is Approved — cleared on
          // any other status, exactly like Scope/Milestones clear it on Unapprove. Earlier
          // this was left in place after moving off Approved, on the theory that the row
          // should still answer "was this ever approved" — but that's wrong: a chip reading
          // "Approved" next to a Status of "Proposed" just looks like a bug. "Was this ever
          // approved, and by whom" is exactly what `history` below now answers instead, so
          // the current-state field doesn't have to double as the historical record too.
          approvedBy: args.status === 'Approved' ? currentPersonId : null,
          approvedAt: args.status === 'Approved' ? new Date().toISOString() : null,
          // Logged on every status change, not just Approved — this is the full log, not
          // just the latest approval.
          history: appendHistory(r.history, `Status → ${args.status}`),
        } : r)),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['initiative', id] }),
    onError: (e: any) => toast.error(e.message || 'Failed to update status'),
  });

  /** Scope items and milestones have no status value that means "approved" (scope has no
   *  status control at all; a milestone's status tracks Done/Missed/At Risk, not sign-off) —
   *  so both get an explicit approve/unapprove action instead of piggybacking on a dropdown,
   *  unlike requirements above. Kept generic since the four fields it can touch
   *  (inScope/outOfScope/milestones, plus reuse for requirements would work too) are all the
   *  same shape of edit: replace one item inside one whole-array field. */
  const setItemApproval = useMutation({
    mutationFn: (args: { kind: 'inScope' | 'outOfScope' | 'milestones'; itemId: string; approve: boolean }) =>
      initiativesApi.update(id!, {
        [args.kind]: ((initiative?.[args.kind] as { id: string; history?: HistoryEntry[] }[]) || []).map((item) => (item.id === args.itemId ? {
          ...item,
          approvedBy: args.approve ? currentPersonId : null,
          approvedAt: args.approve ? new Date().toISOString() : null,
          history: appendHistory(item.history, args.approve ? 'Approved' : 'Unapproved'),
        } : item)),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['initiative', id] }),
    onError: (e: any) => toast.error(e.message || 'Failed to update approval'),
  });

  const deleteInitiativeMutation = useMutation({
    mutationFn: () => initiativesApi.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['initiatives'] });
      toast.success('Initiative deleted.');
      navigate('/tracker/initiatives');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to delete initiative'),
  });

  const deleteReportMutation = useMutation({
    mutationFn: (reportId: string) => reportsApi.delete(id!, reportId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports', id] });
      queryClient.invalidateQueries({ queryKey: ['initiative', id] });
      setConfirmDeleteReport(null);
      toast.success('Report deleted.');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to delete report'),
  });

  // Keeps the "Total Annual Spend" Metric (assetSpendMetricId) in sync with the live rollup
  // computed above - created the first time an asset is linked, and re-read every time this
  // page is visited afterward. This is a pull-on-view sync, not an event-driven one: nothing
  // pushes an update here the moment a linked asset's billing changes elsewhere, only the next
  // time someone opens this initiative and the computed total has actually moved.
  const syncAssetSpendMetricMutation = useMutation({
    mutationFn: async () => {
      let metricId = initiative?.assetSpendMetricId;
      if (!metricId) {
        const created = await metricsApi.create({
          name: `${initiative!.name} — Total Annual Spend`,
          unit: 'USD',
          direction: 'lower',
          origin: initiative!.tier,
          originInitiativeId: initiative!.initiativeId,
          trackedAsBau: true,
          bauOwnerId: initiative!.ownerId,
          criticality: 'Medium',
        });
        metricId = created.metricId;
        await initiativesApi.update(id!, {
          assetSpendMetricId: metricId,
          targetMetricIds: [...(initiative!.targetMetricIds || []), metricId],
        });
      }
      await readingsApi.create(metricId!, { date: todayIso(), value: Math.round(totalAnnualSpendRollup) });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['initiative', id] });
      queryClient.invalidateQueries({ queryKey: ['metrics'] });
      queryClient.invalidateQueries({ queryKey: ['initiatives'] });
    },
  });
  const assetSpendMetric = targetMetrics.find((m: any) => m.metricId === initiative?.assetSpendMetricId);
  const assetSpendReadings: any[] = assetSpendMetric ? (readingsByMetric[assetSpendMetric.metricId] || []) : [];
  const latestAssetSpendReading = assetSpendReadings[assetSpendReadings.length - 1];
  useEffect(() => {
    if (!initiative || !linkedAssetsLoaded || linkedAssets.length === 0) return;
    if (syncAssetSpendMetricMutation.isPending) return;
    const upToDate = !!initiative.assetSpendMetricId
      && !!latestAssetSpendReading
      && latestAssetSpendReading.date === todayIso()
      && Math.round(latestAssetSpendReading.value) === Math.round(totalAnnualSpendRollup);
    if (!upToDate) syncAssetSpendMetricMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initiative?.initiativeId, initiative?.assetSpendMetricId, linkedAssetsLoaded, linkedAssets.length, totalAnnualSpendRollup, latestAssetSpendReading?.date, latestAssetSpendReading?.value]);

  const createMetricMutation = useMutation({
    mutationFn: async () => {
      const created = await metricsApi.create({
        name: newMetricForm.name,
        unit: newMetricForm.unit,
        direction: newMetricForm.direction,
        cadence: newMetricForm.cadence,
        target: newMetricForm.target ? Number(newMetricForm.target) : undefined,
        baseline: newMetricForm.baseline ? Number(newMetricForm.baseline) : undefined,
        criticality: newMetricForm.criticality,
        origin: 'Project',
        originInitiativeId: id,
        // A Program/Portfolio is ongoing by nature - its own metrics are tracked from the
        // moment they're created, no "Send to Catalog" step needed (see the send-to-catalog
        // action below, which only ever shows for a metric that isn't trackedAsBau yet). A
        // Project's metric proves delivery and stays untracked until explicitly sent.
        trackedAsBau: initiative?.tier === 'Program' || initiative?.tier === 'Portfolio',
        bauOwnerId: (initiative?.tier === 'Program' || initiative?.tier === 'Portfolio') ? initiative?.ownerId : undefined,
      });
      // Creating a metric here is meaningless if it doesn't also show up in THIS initiative's
      // own Metrics card - metricsApi.create only writes the catalog record, it has no idea
      // which initiative's targetMetricIds should reference it back.
      await initiativesApi.update(id!, { targetMetricIds: [...(initiative?.targetMetricIds || []), created.metricId] });
      return created;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metrics'] });
      queryClient.invalidateQueries({ queryKey: ['initiative', id] });
      // Also the plural, tenant-wide list (CatalogPage and this page's own allInitiatives
      // both read from it) - this initiative's targetMetricIds just changed too, via the
      // initiativesApi.update above, and that list was the one cache this mutation forgot
      // to invalidate, unlike its sibling sendToCatalogMutation below.
      queryClient.invalidateQueries({ queryKey: ['initiatives'] });
      setShowNewMetricForm(false);
      setNewMetricForm({ name: '', unit: '', direction: 'higher', cadence: 'Monthly', target: '', baseline: '', criticality: 'Medium' });
      toast.success('Metric created.');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to create metric'),
  });

  // Reuses update_metric's existing allow-list (bauOwnerId/cadence/trackedAsBau are already
  // writable there) rather than needing a new endpoint - "send to catalog" is just setting
  // those three fields, available any time, not only bundled into Mark Completed below.
  const sendToCatalogMutation = useMutation({
    mutationFn: async (args: { metricId: string; bauOwnerId: string; cadence: string }) => {
      await metricsApi.update(args.metricId, { trackedAsBau: true, bauOwnerId: args.bauOwnerId, cadence: args.cadence });
      // Sending a Project's metric forward is meaningless if the Program it graduates to
      // can't actually see it - link it into the parent's own targetMetricIds too (Operational
      // Health only ever reads an initiative's OWN linked metrics, never its children's), while
      // leaving it on THIS initiative's list untouched so the Project still shows what it
      // delivered. Available any time from this tab, not gated behind Mark Completed.
      if (parentInitiative && !(parentInitiative.targetMetricIds || []).includes(args.metricId)) {
        await initiativesApi.update(parentInitiative.initiativeId, {
          targetMetricIds: [...(parentInitiative.targetMetricIds || []), args.metricId],
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['metrics'] });
      queryClient.invalidateQueries({ queryKey: ['initiatives'] });
      if (parentInitiative) queryClient.invalidateQueries({ queryKey: ['initiative', parentInitiative.initiativeId] });
      setSendingToCatalog(null);
      toast.success(parentInitiative ? `Metric sent to the Service Catalog and linked to ${parentInitiative.name}.` : 'Metric sent to the Service Catalog.');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to send metric to the catalog'),
  });

  const completeMutation = useMutation({
    mutationFn: (handoffs: { metricId: string; bauOwnerId: string; cadence: string }[]) => initiativesApi.complete(id!, handoffs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['initiative', id] });
      queryClient.invalidateQueries({ queryKey: ['metrics'] });
      // complete_initiative now also links any handed-off metric into the parent's own
      // targetMetricIds server-side (mirroring sendToCatalogMutation) - the plural,
      // tenant-wide list and the parent's own cached detail need invalidating too, or the
      // Catalog/parent Programme keep showing the pre-handoff state until they go stale.
      queryClient.invalidateQueries({ queryKey: ['initiatives'] });
      if (parentInitiative) queryClient.invalidateQueries({ queryKey: ['initiative', parentInitiative.initiativeId] });
      toast.success('Initiative marked complete — target metrics handed off to BAU tracking.');
      setShowComplete(false);
    },
    onError: (e: any) => toast.error(e.message || 'Failed to complete'),
  });

  const approveExceptionMutation = useMutation({
    mutationFn: (exceptionId: string) => initiativesApi.approveException(id!, exceptionId, currentPersonId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['initiative', id] });
      queryClient.invalidateQueries({ queryKey: ['initiatives'] });
      toast.success('Exception approved — projected end date updated.');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to approve exception'),
  });

  const rejectExceptionMutation = useMutation({
    mutationFn: (exceptionId: string) => initiativesApi.rejectException(id!, exceptionId, currentPersonId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['initiative', id] });
      toast.success('Exception rejected.');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to reject exception'),
  });

  if (!initiative) return <p className="np-sub container-xxl flex-grow-1 container-p-y">Loading…</p>;

  const personName = (pid: string) => people.find((p: any) => p.personId === pid)?.personName || 'Unassigned';
  const milestoneName = (mid: string) => (initiative.milestones || []).find((m) => m.id === mid)?.name || mid;
  const stripHtml = (htmlValue: string) => (htmlValue || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  const milestoneWeightedCompletion = milestoneCompletion(initiative.milestones || []);
  const ciHealthScore = ciHealth(childInitiatives);
  const opHealth = operationalHealth(rollupMetrics, (m) => computeMetricStatus(m, readingsByMetric[(m as any).metricId] || []));
  // Monday of the current week, same convention SubmitReportPage.tsx/SubmitRollupReportPage.tsx
  // use for a report's own weekOf - just for the "filed this week?" status line below.
  const currentWeekOf = (() => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff)).toISOString().slice(0, 10);
  })();
  const thisWeekReport = reports.find((r) => r.weekOf === currentWeekOf);
  // floor is a severity FLOOR (never let the label look better than it), not a cap - so it only
  // ever pulls the numeric tone *up* in severity, never down. Tone ordering is success < warning
  // < danger; 'Blocked' always wins (it's the worst), 'At Risk' only overrides a numeric tone
  // that's currently 'success' (warning/danger are already at least as severe as At Risk).
  const opTone = opHealth.score === null
    ? null
    : opHealth.floor === 'Blocked'
      ? 'danger'
      : opHealth.floor === 'At Risk' && scoreTone(opHealth.score) === 'success'
        ? 'warning'
        : scoreTone(opHealth.score);

  // Weekly progression: how much % complete was gained since the last report, versus
  // how much of the total planned schedule was consumed in that same interval (i.e.
  // "burn rate" — the pace you'd need to sustain to finish on time). If burn rate keeps
  // outrunning completion gained, the initiative is spending schedule faster than it's
  // making progress.
  // Burn-up view: cumulative % complete (bars) against cumulative % of the total planned
  // schedule consumed (line), both on the same 0-100 scale — bars sitting below the line
  // means the initiative is behind pace; above it means ahead.
  const progressSeries = (() => {
    const sorted = [...reports].sort((a, b) => a.weekOf.localeCompare(b.weekOf));
    if (sorted.length === 0) return [];
    const start = new Date(initiative.startDate || initiative.createdAt).getTime();
    const totalEnd = new Date(initiative.originalTargetEndDate || initiative.currentProjectedEndDate || initiative.createdAt).getTime();
    const totalDurationDays = Math.max((totalEnd - start) / 86400000, 1);

    return sorted.map((r) => {
      const reportDate = new Date(r.weekOf).getTime();
      const daysElapsed = Math.max((reportDate - start) / 86400000, 0);
      const scheduleConsumed = Math.round(Math.min((daysElapsed / totalDurationDays) * 100, 100) * 10) / 10;
      return { weekOf: r.weekOf, cumulativeComplete: r.percentComplete, scheduleConsumed, health: r.health };
    });
  })();

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-flashlight-line"
        title={initiative.name}
        subtitle={`${initiative.initiativeId} · ${initiative.category}`}
        breadcrumbs={[
          { label: 'Initiative Tracker', to: '/tracker' },
          { label: 'All Initiatives', to: '/tracker/initiatives' },
          { label: initiative.name },
        ]}
        actions={
          <div className="d-flex gap-2">
            <button className="np-btn np-btn-ghost" onClick={() => navigate(`/tracker/initiatives/${id}/edit`)}>
              <i className="ri-edit-line me-1"></i>Edit
            </button>
            {initiative.status !== 'Completed' && (
              <>
                <button className="np-btn np-btn-primary" onClick={() => navigate(`/tracker/initiatives/${id}/report`)}>
                  <i className="ri-checkbox-circle-line me-1"></i>Submit this week's report
                </button>
                {/* Completing hands target metrics off to BAU tracking - meaningless for
                    something that's already permanently ongoing and never wraps up. */}
                {!initiative.isBau && (
                  <button className="np-btn np-btn-ghost-success" onClick={() => setShowComplete(true)}>
                    <i className="ri-flag-line me-1"></i>Mark Completed
                  </button>
                )}
              </>
            )}
            <button className="np-icon-btn np-icon-btn-danger" title="Delete initiative" onClick={() => setConfirmDeleteInitiative(true)}>
              <i className="ri-delete-bin-line"></i>
            </button>
          </div>
        }
      />

      {/* The one-line summary ("the one sentence that explains this initiative to someone who
          has never heard of it", per its own placeholder on the edit form) only ever rendered
          on the edit form - never here, where someone unfamiliar with the initiative actually
          lands. Shown as its own line since PageHeader's subtitle is already the ID/category
          line below the title. */}
      {initiative.summary && <p className="mb-3">{initiative.summary}</p>}

      {/* Status chips — PageHeader's subtitle is plain-string only, so the status/health/
          priority chips (each needs its own tone class) render in their own row right after
          the header, same pattern as AssetDetailPage.tsx's "ID subtitle" row. */}
      <div className="d-flex gap-2 flex-wrap mb-3">
        {initiative.isBau && (
          <span className="np-chip np-tone-neutral"><i className="ri-repeat-line me-1"></i>Standalone Work</span>
        )}
        <span className={`np-chip np-tone-${toneOf(STATUS_COLOR[initiative.status])}`}>{initiative.status}</span>
        <span className={`np-chip np-tone-${toneOf(HEALTH_COLOR[effectiveHealth(initiative)])}`}>{effectiveHealth(initiative)}</span>
        <span className={`np-chip np-tone-${toneOf(PRIORITY_COLOR[initiative.priority])}`}>{initiative.priority} Priority</span>
        {initiative.tier && (
          <span className="np-chip np-tone-primary"><i className="ri-stack-line me-1"></i>{initiative.tier}</span>
        )}
        {parentInitiative && (
          <Link to={`/tracker/initiatives/${parentInitiative.initiativeId}`} className="np-chip np-tone-neutral">
            <i className="ri-corner-left-up-line me-1"></i>{parentInitiative.tier || 'Parent'}: {parentInitiative.name}
          </Link>
        )}
        {childInitiatives.length > 0 && (
          <span className="np-chip np-tone-neutral">
            <i className="ri-corner-right-down-line me-1"></i>{childInitiatives.length} linked {childInitiatives.length === 1 ? 'child' : 'children'}
          </span>
        )}
      </div>

      {confirmDeleteInitiative && (
        <div className="np-banner np-tone-danger d-flex align-items-center gap-2 mb-3">
          <i className="ri-alarm-warning-line"></i>
          <span className="flex-grow-1">Delete "{initiative.name}" permanently? This removes the initiative and all of its reports.</span>
          <button className="np-btn np-btn-sm np-btn-danger ms-2" disabled={deleteInitiativeMutation.isPending} onClick={() => deleteInitiativeMutation.mutate()}>Yes, delete</button>
          <button className="np-btn np-btn-sm np-btn-ghost" onClick={() => setConfirmDeleteInitiative(false)}>Cancel</button>
        </div>
      )}

      {initiative.pendingProjectedEndDate && (
        <div className="np-banner np-tone-warning d-flex align-items-center gap-2 mb-3">
          <i className="ri-time-line"></i>
          <span className="flex-grow-1">A change to the projected end date ({formatDate(initiative.pendingProjectedEndDate)}) is pending approval.</span>
          <button className="np-btn np-btn-sm np-btn-ghost" onClick={() => setTab('Exceptions')}>Review</button>
        </div>
      )}

      {showComplete && (
        <CompleteInitiativePanel
          initiative={initiative}
          targetMetrics={targetMetrics}
          people={people}
          onCancel={() => setShowComplete(false)}
          onConfirm={(handoffs) => completeMutation.mutate(handoffs)}
          pending={completeMutation.isPending}
        />
      )}

      <div className="d-flex gap-4 border-bottom mb-4" style={{ fontSize: 13.5 }}>
        {/* Timeline plots start/target-end dates - a Portfolio or Programme doesn't carry its
            own dates the way a Project does, so the card has nothing to show there. */}
        {TABS.filter((t) => t !== 'Timeline' || (initiative.tier !== 'Portfolio' && initiative.tier !== 'Program')).map((t) => (
          <div
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '9px 2px', cursor: 'pointer', fontWeight: tab === t ? 700 : 500,
              color: tab === t ? 'var(--bs-primary)' : 'var(--bs-body-color)',
              borderBottom: tab === t ? '2px solid var(--bs-primary)' : '2px solid transparent',
            }}
          >
            {t}
            {t === 'Actions' && openActions.length > 0 && (
              <span className="np-chip np-tone-warning ms-1" style={{ fontSize: '0.65rem' }}>{openActions.length}</span>
            )}
            {t === 'Decisions' && decisionEntries.length > 0 && (
              <span className="np-chip np-tone-neutral ms-1" style={{ fontSize: '0.65rem' }}>{decisionEntries.length}</span>
            )}
          </div>
        ))}
      </div>

      {tab === 'Overview' && (
        <div className="row g-3">
          <div className="col-lg-8">
            <div className="np-card mb-3">
              <div className="np-card-head"><h2 className="mb-0">Description</h2></div>
              <div className="np-card-body">
                <RichTextView
                  value={initiative.description}
                  empty={
                    <div className="text-center np-sub py-3">
                      <i className="ri-file-text-line d-block mb-2" style={{ fontSize: '1.5rem' }}></i>
                      No description.
                    </div>
                  }
                />
              </div>
            </div>

            {/* Current/Desired Situation — read-only here, same as Description above. Editing
                happens on the Edit Initiative form (InitiativeFormPage.tsx), not in place on
                this view page - every other scalar field on this page follows that same
                edit-elsewhere convention, and these two are no exception. Side-by-side so the
                "what exists" / "what should exist" contrast reads at a glance. Attachments use
                DocumentManager's embedded mode so they read as part of THIS card (a divider
                inside the same np-card-body) rather than a visually separate card underneath -
                matching the approved concept's single-unified-card look - and its readOnly
                mode, since uploading/removing a file is an edit action that belongs on the
                Edit form (below, alongside its own DocumentManager) same as everything else. */}
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <div className="np-card mb-3 np-card-accent-neutral">
                  <div className="np-card-head">
                    <span className="np-icon-badge np-icon-badge-sm np-tone-neutral me-2"><i className="ri-map-2-line"></i></span>
                    <h2 className="mb-0">Current Situation</h2>
                  </div>
                  <div className="np-card-body">
                    <RichTextView
                      value={initiative.currentSituation}
                      empty={
                        <div className="text-center np-sub py-3">
                          <i className="ri-map-2-line d-block mb-2" style={{ fontSize: '1.5rem' }}></i>
                          No current situation recorded yet.
                        </div>
                      }
                    />
                    {/* embedded: reads as part of THIS card (a divider + label, not a second
                        card underneath) - matches the approved concept's unified-card layout. */}
                    <DocumentManager embedded readOnly entityType="initiative_current_situation" entityId={initiative.initiativeId} />
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div className="np-card mb-3 np-card-accent-primary">
                  <div className="np-card-head">
                    <span className="np-icon-badge np-icon-badge-sm np-tone-primary me-2"><i className="ri-flag-line"></i></span>
                    <h2 className="mb-0">Desired Situation</h2>
                  </div>
                  <div className="np-card-body">
                    <RichTextView
                      value={initiative.desiredSituation}
                      empty={
                        <div className="text-center np-sub py-3">
                          <i className="ri-flag-line d-block mb-2" style={{ fontSize: '1.5rem' }}></i>
                          No desired situation recorded yet.
                        </div>
                      }
                    />
                    <DocumentManager embedded readOnly entityType="initiative_desired_situation" entityId={initiative.initiativeId} />
                  </div>
                </div>
              </div>
            </div>

            <div className="np-card mb-3">
              <div className="np-card-head"><h2 className="mb-0">Scope</h2></div>
              <div className="np-card-body pt-2">
                <NimbusTable
                  thead={<tr><th>Scope</th><th>Item</th><th>History</th></tr>}
                  isEmpty={(initiative.inScope?.length || 0) + (initiative.outOfScope?.length || 0) === 0}
                  emptyState={<div className="text-center np-sub py-3">
                    <i className="ri-focus-3-line d-block mb-2" style={{ fontSize: '1.5rem' }}></i>
                    None recorded.
                  </div>}
                >
                  {(initiative.inScope || []).map((item) => (
                    <ScopeRow
                      key={item.id}
                      item={item}
                      inScope
                      approverName={personName}
                      onApprove={(itemId) => setItemApproval.mutate({ kind: 'inScope', itemId, approve: true })}
                      onUnapprove={(itemId) => setItemApproval.mutate({ kind: 'inScope', itemId, approve: false })}
                      busy={setItemApproval.isPending && setItemApproval.variables?.itemId === item.id}
                    />
                  ))}
                  {(initiative.outOfScope || []).map((item) => (
                    <ScopeRow
                      key={item.id}
                      item={item}
                      inScope={false}
                      approverName={personName}
                      onApprove={(itemId) => setItemApproval.mutate({ kind: 'outOfScope', itemId, approve: true })}
                      onUnapprove={(itemId) => setItemApproval.mutate({ kind: 'outOfScope', itemId, approve: false })}
                      busy={setItemApproval.isPending && setItemApproval.variables?.itemId === item.id}
                    />
                  ))}
                </NimbusTable>
              </div>
            </div>
            <div className="np-card mb-3">
              <div className="np-card-head"><h2 className="mb-0">Functional Requirements</h2></div>
              <div className="np-card-body pt-2">
                <NimbusTable
                  thead={<tr><th>Requirement</th><th>Status</th><th>History</th></tr>}
                  isEmpty={initiative.functionalRequirements.length === 0}
                  emptyState={<div className="text-center np-sub py-3">
                    <i className="ri-list-check-2 d-block mb-2" style={{ fontSize: '1.5rem' }}></i>
                    None recorded.
                  </div>}
                >
                  {initiative.functionalRequirements.map((r) => (
                    <RequirementRow key={r.id} req={r} kind="functionalRequirements" approverName={personName} onStatusChange={(status) => updateRequirementStatus.mutate({ kind: 'functionalRequirements', reqId: r.id, status })} />
                  ))}
                </NimbusTable>
              </div>
            </div>
            <div className="np-card mb-3">
              <div className="np-card-head"><h2 className="mb-0">Non-Functional Requirements</h2></div>
              <div className="np-card-body pt-2">
                <NimbusTable
                  thead={<tr><th>Requirement</th><th>Status</th><th>History</th></tr>}
                  isEmpty={initiative.nonFunctionalRequirements.length === 0}
                  emptyState={<div className="text-center np-sub py-3">
                    <i className="ri-list-check-2 d-block mb-2" style={{ fontSize: '1.5rem' }}></i>
                    None recorded.
                  </div>}
                >
                  {initiative.nonFunctionalRequirements.map((r) => (
                    <RequirementRow key={r.id} req={r} kind="nonFunctionalRequirements" approverName={personName} onStatusChange={(status) => updateRequirementStatus.mutate({ kind: 'nonFunctionalRequirements', reqId: r.id, status })} />
                  ))}
                </NimbusTable>
              </div>
            </div>

          </div>
          <div className="col-lg-4">
            {/* Every asset linked to this initiative via Linked Artefacts, rolled up into one
                figure - same shape at every tier (Project/Programme/Portfolio all use the same
                tab). Shown even with zero linked assets, matching every other card's empty-
                state convention on this tab; the underlying Metric (assetSpendMetricId) only
                gets created once there's at least one. */}
            <div className="np-card mb-3">
              <div className="np-card-head">
                <h2 className="mb-0">Asset Spend</h2>
              </div>
              <div className="np-card-body">
                {linkedAssets.length === 0 ? (
                  <div className="text-center np-sub py-2">
                    <i className="ri-server-line d-block mb-1" style={{ fontSize: '1.25rem' }}></i>
                    <span style={{ fontSize: '0.78rem' }}>No assets linked yet — link one on the Linked Artefacts tab.</span>
                  </div>
                ) : (
                  <>
                    <div className="d-flex align-items-baseline justify-content-between mb-2">
                      <span className="fw-semibold" style={{ fontSize: '0.83rem' }}>Total Annual Spend</span>
                      <span className="fw-bold" style={{ fontSize: '1.05rem' }}>{formatUsd(totalAnnualSpendRollup)}</span>
                    </div>
                    <div className="np-sub mb-2" style={{ fontSize: '0.74rem' }}>
                      {linkedAssets.length} linked asset{linkedAssets.length === 1 ? '' : 's'} · this calendar year, converted to USD at a static approximate rate
                    </div>
                    <div className="pt-2" style={{ borderTop: '1px dashed var(--bs-border-color)' }}>
                      {linkedAssets.map((a: any) => {
                        const assetTotal = totalAnnualSpendUsd(a.billingEntries || []);
                        return (
                          <div key={a.assetId} className="d-flex align-items-center gap-2 py-1" style={{ fontSize: '0.78rem' }}>
                            <span className="text-truncate flex-grow-1">{a.name}</span>
                            <span className="np-tabular np-sub">{formatUsd(assetTotal)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Continuous Improvement Health (delivery, from direct children) and Operational
                Health (Service Catalog metrics this initiative itself targets, weighted by
                criticality) - both always computed live, never independently stored/edited,
                same convention as milestoneWeightedCompletion above. Only meaningful once
                there's a hierarchy or metrics underneath, so Project-tier initiatives (which
                have neither) don't get this card at all. */}
            {(initiative.tier === 'Program' || initiative.tier === 'Portfolio') && (
              <div className="np-card mb-3">
                <div className="np-card-head">
                  <h2 className="mb-0">Health Rollup</h2>
                </div>
                <div className="np-card-body">
                  <div className="pb-3 mb-3" style={{ borderBottom: '1px solid var(--bs-border-color)' }}>
                    <div className="d-flex align-items-baseline justify-content-between mb-2">
                      <span className="fw-semibold" style={{ fontSize: '0.83rem' }}>Continuous Improvement Health</span>
                      <span className="fw-bold" style={{ fontSize: '1.05rem', color: ciHealthScore === null ? undefined : `var(--bs-${scoreTone(ciHealthScore)})` }}>
                        {ciHealthScore === null ? '—' : `${ciHealthScore}%`}
                      </span>
                    </div>
                    {ciHealthScore === null ? (
                      <div className="text-center np-sub py-2">
                        <i className="ri-inbox-line d-block mb-1" style={{ fontSize: '1.25rem' }}></i>
                        <span style={{ fontSize: '0.78rem' }}>No active {initiative.tier === 'Portfolio' ? 'programmes' : 'projects'} underneath yet.</span>
                      </div>
                    ) : (
                      <>
                        <div className="np-progress mb-2">
                          <div className={`np-fill np-tone-${scoreTone(ciHealthScore)}`} style={{ width: `${ciHealthScore}%` }}></div>
                        </div>
                        <div className="np-sub" style={{ fontSize: '0.74rem' }}>
                          {childInitiatives.filter((c) => effectiveHealth(c) === 'On Track').length} of{' '}
                          {childInitiatives.filter((c) => effectiveHealth(c) !== 'Not Started').length} active{' '}
                          {initiative.tier === 'Portfolio' ? 'programmes' : 'projects'} on track or better
                        </div>
                      </>
                    )}
                  </div>

                  <div>
                    <div className="d-flex align-items-baseline justify-content-between mb-2">
                      <span className="fw-semibold" style={{ fontSize: '0.83rem' }}>Operational Health</span>
                      <span className="fw-bold" style={{ fontSize: '1.05rem', color: opTone ? `var(--bs-${opTone})` : undefined }}>
                        {opHealth.score === null ? '—' : `${opHealth.score}%`}
                      </span>
                    </div>
                    {opHealth.score === null ? (
                      <div className="text-center np-sub py-2">
                        <i className="ri-inbox-line d-block mb-1" style={{ fontSize: '1.25rem' }}></i>
                        <span style={{ fontSize: '0.78rem' }}>Not enough data yet — link Service Catalog metrics to this initiative to see it here.</span>
                      </div>
                    ) : (
                      <>
                        <div className="np-progress mb-2">
                          <div className={`np-fill np-tone-${opTone}`} style={{ width: `${opHealth.score}%` }}></div>
                        </div>
                        {opHealth.floor && (
                          <div className="np-sub d-flex align-items-center gap-1" style={{ fontSize: '0.74rem', color: `var(--bs-${opHealth.floor === 'Blocked' ? 'danger' : 'warning'}-text-emphasis)` }}>
                            <i className="ri-alert-line"></i>
                            Capped by {rollupMetrics.filter((m: any) => (m.criticality || 'Medium') === (opHealth.floor === 'Blocked' ? 'Critical' : 'High') && computeMetricStatus(m, readingsByMetric[m.metricId] || []) === 'off-target').length}{' '}
                            {opHealth.floor === 'Blocked' ? 'Critical' : 'High'}-criticality metric(s) off-target
                          </div>
                        )}
                      </>
                    )}
                    {rollupMetrics.length > 0 && (
                      <div className="mt-2 pt-2" style={{ borderTop: '1px dashed var(--bs-border-color)' }}>
                        {rollupMetrics.map((m: any) => {
                          const status = computeMetricStatus(m, readingsByMetric[m.metricId] || []);
                          if (status !== 'on-target' && status !== 'off-target') return null;
                          return (
                            <div key={m.metricId} className="d-flex align-items-center gap-2 py-1" style={{ fontSize: '0.78rem' }}>
                              <span className="text-truncate flex-grow-1">{m.name}</span>
                              <span className={`np-chip np-tone-${SEVERITY_TONE[(m.criticality || 'Medium') as keyof typeof SEVERITY_TONE]}`} style={{ fontSize: '0.62rem' }}>
                                {m.criticality || 'Medium'}
                              </span>
                              <span className={`np-chip np-tone-${status === 'on-target' ? 'success' : 'danger'}`} style={{ fontSize: '0.62rem' }}>
                                {status === 'on-target' ? 'On target' : 'Off target'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className={`np-card mb-3 ${initiative.milestones.some((m) => m.status === 'Missed') ? 'np-card-accent-danger' : initiative.milestones.some((m) => m.status === 'At Risk') ? 'np-card-accent-warning' : ''}`}>
              <div className="np-card-head" style={{ justifyContent: 'space-between' }}>
                <h2 className="mb-0">Milestones</h2>
                {milestoneWeightedCompletion !== null && (
                  <span className="np-sub small">{milestoneWeightedCompletion}% weighted</span>
                )}
              </div>
              <div className="np-card-body">
                {initiative.milestones.length === 0 && (
                  <div className="text-center np-sub py-3">
                    <i className="ri-flag-line d-block mb-2" style={{ fontSize: '1.5rem' }}></i>
                    None recorded.
                  </div>
                )}
                {milestoneWeightedCompletion !== null && (
                  <div className="np-progress mb-3">
                    <div className="np-fill np-tone-success" style={{ width: `${milestoneWeightedCompletion}%` }}></div>
                  </div>
                )}
                {initiative.milestones.map((m, i) => (
                  <div
                    key={m.id}
                    className="py-2"
                    style={{ borderBottom: i === initiative.milestones.length - 1 ? 'none' : '1px solid var(--bs-border-color)' }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-2">
                        <span className="dot" style={{ width: 8, height: 8, borderRadius: '50%', background: m.status === 'Done' ? 'var(--bs-success)' : m.status === 'Missed' ? 'var(--bs-danger)' : m.status === 'At Risk' ? 'var(--bs-warning)' : 'var(--bs-secondary)', display: 'inline-block' }}></span>
                        {m.name}
                      </div>
                      <span className="np-sub" style={{ fontSize: 11.5 }}>
                        {m.status} · {formatDate(m.targetDate)}{!!m.weight && ` · ${m.weight}% weight`}
                      </span>
                    </div>
                    <div className="d-flex align-items-center justify-content-between gap-2 mt-1">
                      <div className="d-flex align-items-center gap-2">
                        <div className="np-progress" style={{ width: 60 }}>
                          <div className="np-fill" style={{ width: `${milestoneProgress(m)}%` }}></div>
                        </div>
                        <span className="np-sub" style={{ fontSize: 11 }}>{milestoneProgress(m)}% complete</span>
                      </div>
                      {/* Independent of status — a milestone can be Done with nobody having
                          signed off on it yet, so this is a separate action, not derived from m.status. */}
                      <HistoryPopoverTrigger
                        approvedBy={m.approvedBy}
                        history={m.history}
                        approverName={personName}
                        onApprove={() => setItemApproval.mutate({ kind: 'milestones', itemId: m.id, approve: true })}
                        onUnapprove={() => setItemApproval.mutate({ kind: 'milestones', itemId: m.id, approve: false })}
                        busy={setItemApproval.isPending && setItemApproval.variables?.itemId === m.id}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="np-card">
              <div className="np-card-head"><h2 className="mb-0">Stakeholders</h2></div>
              <div className="np-card-body">
                {initiative.stakeholders.length === 0 && (
                  <div className="text-center np-sub py-3">
                    <i className="ri-group-line d-block mb-2" style={{ fontSize: '1.5rem' }}></i>
                    None recorded.
                  </div>
                )}
                {initiative.stakeholders.map((s, i) => (
                  <div
                    key={i}
                    className="d-flex align-items-center gap-2 py-2"
                    style={{ borderBottom: i === initiative.stakeholders.length - 1 ? 'none' : '1px solid var(--bs-border-color)' }}
                  >
                    <span className="avatar-init xs" style={{ background: 'color-mix(in srgb, var(--bs-paper-bg) 84%, var(--bs-primary))', color: 'var(--bs-primary)' }}>{initials(s.name)}</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{s.name}</div>
                      <div className="np-sub" style={{ fontSize: 11.5 }}>{s.role}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Meeting minutes pinned to this initiative. */}
      {tab === 'Minutes' && (
        <div className="np-card">
          <div className="np-card-body">
            {minutesLoading ? (
              <div className="text-center np-sub py-4">Loading…</div>
            ) : pinnedMinutesRollup.length === 0 ? (
              <div className="text-center np-sub py-4">
                <i className="ri-file-list-3-line d-block mb-2" style={{ fontSize: '1.75rem' }}></i>
                No meetings pinned to this initiative{initiative.tier === 'Program' ? ' or its projects' : ''} yet.
                <div className="mt-3">
                  <Link className="np-btn np-btn-sm np-btn-primary" to="/meeting-minutes/new">
                    Record a meeting
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="np-sub small">
                    {pinnedMinutesRollup.length} meeting{pinnedMinutesRollup.length === 1 ? '' : 's'}
                    {pinnedOpenActionCount > 0 && ` · ${pinnedOpenActionCount} open action${pinnedOpenActionCount === 1 ? '' : 's'}`}
                  </span>
                  <Link className="np-btn np-btn-sm np-btn-primary" to="/meeting-minutes/new">
                    Record a meeting
                  </Link>
                </div>
                <NimbusTable
                  thead={
                    <tr>
                      <th style={{ width: '110px' }}>Date</th>
                      <th>Title</th>
                      {initiative.tier === 'Program' && <th style={{ width: '160px' }}>Project</th>}
                      <th style={{ width: '110px' }}>Entries</th>
                      <th style={{ width: '130px' }}>Open actions</th>
                      <th style={{ width: '110px' }}>Status</th>
                    </tr>
                  }
                >
                  {pinnedMinutesRollup.map((m) => {
                    const open = (m.entries || []).filter(
                      (e) => e.category === 'action' && e.status === 'open',
                    ).length;
                    const sourceName = rollupSourceName(m.initiativeId);
                    return (
                      <tr key={m.minutesId} style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/meeting-minutes/${m.minutesId}`)}>
                        <td>{formatDate(m.meetingDate)}</td>
                        <td>{m.title || <span className="np-sub">Untitled</span>}</td>
                        {initiative.tier === 'Program' && (
                          <td className="np-sub">{sourceName || <span className="fst-italic">This programme</span>}</td>
                        )}
                        <td>{(m.entries || []).length}</td>
                        <td>{open > 0
                          ? <span className="np-chip np-tone-warning">{open}</span>
                          : <span className="np-sub">—</span>}</td>
                        <td><span className="np-chip np-tone-neutral">{m.status}</span></td>
                      </tr>
                    );
                  })}
                </NimbusTable>
              </>
            )}
          </div>
        </div>
      )}

      {tab === 'Actions' && (
        <div className="np-card">
          <div className="np-card-body">
            {(initiative.actions || []).length === 0 && (
              <div className="text-center np-sub py-4">
                <i className="ri-check-double-line d-block mb-2" style={{ fontSize: '1.75rem' }}></i>
                No actions recorded directly on this initiative.
              </div>
            )}
            {(initiative.actions || []).map((a: Action) =>
              editingAction === a.id && actionForm ? (
                <div key={a.id} className="d-flex align-items-center gap-2 py-2 border-bottom flex-wrap">
                  <input
                    className="np-input flex-grow-1"
                    style={{ minWidth: 180 }}
                    value={actionForm.description}
                    autoFocus
                    onChange={(e) => setActionForm({ ...actionForm, description: e.target.value })}
                  />
                  <div style={{ width: 170 }}>
                    <Select
                      options={people.map((p: any) => ({ value: p.personId, label: p.personName }))}
                      value={(() => {
                        const opt = people.find((p: any) => p.personId === actionForm.ownerId);
                        return opt ? { value: opt.personId, label: opt.personName } : null;
                      })()}
                      onChange={(opt) => setActionForm({ ...actionForm, ownerId: opt?.value ?? '' })}
                      placeholder="— owner —"
                      isClearable
                      isSearchable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={reactSelectStyles()}
                    />
                  </div>
                  <input
                    type="date"
                    className="np-input"
                    style={{ width: 150 }}
                    value={actionForm.dueDate || ''}
                    onChange={(e) => setActionForm({ ...actionForm, dueDate: e.target.value })}
                  />
                  <select
                    className="np-select"
                    style={{ width: 110 }}
                    value={actionForm.status}
                    onChange={(e) => setActionForm({ ...actionForm, status: e.target.value as Action['status'] })}
                  >
                    {['Open', 'Done'].map((s) => <option key={s}>{s}</option>)}
                  </select>
                  <button
                    className="np-btn np-btn-sm np-btn-primary"
                    disabled={!actionForm.description.trim() || updateActionMutation.isPending}
                    onClick={() => updateActionMutation.mutate(actionForm)}
                  >
                    Save
                  </button>
                  <button className="np-btn np-btn-sm np-btn-ghost" onClick={() => { setEditingAction(null); setActionForm(null); }}>Cancel</button>
                </div>
              ) : (
                <div key={a.id} className="d-flex align-items-center justify-content-between py-2 border-bottom flex-wrap gap-2">
                  <span>
                    {a.description}
                    <span className="np-sub ms-2" style={{ fontSize: 12 }}>
                      {a.ownerId ? personName(a.ownerId) : 'Unassigned'}
                      {a.dueDate && ` · due ${formatDate(a.dueDate)}`}
                    </span>
                  </span>
                  <span className="d-flex align-items-center gap-2">
                    <span className={`np-chip np-tone-${a.status === 'Done' ? 'success' : 'warning'}`}>{a.status}</span>
                    {a.status === 'Open' && (
                      <button className="np-btn np-btn-sm np-btn-ghost-success" disabled={updateActionMutation.isPending} onClick={() => updateActionMutation.mutate({ ...a, status: 'Done' })}>
                        <i className="ri-check-line"></i> Mark done
                      </button>
                    )}
                    <button className="np-icon-btn" title="Edit" onClick={() => { setEditingAction(a.id); setActionForm(a); }}>
                      <i className="ri-edit-line"></i>
                    </button>
                    {confirmDeleteAction === a.id ? (
                      <>
                        <span className="text-danger" style={{ fontSize: 12 }}>Delete?</span>
                        <button className="np-btn np-btn-sm np-btn-ghost text-danger p-0" style={{ fontSize: 12 }} disabled={deleteActionMutation.isPending} onClick={() => deleteActionMutation.mutate(a.id)}>Yes</button>
                        <button className="np-btn np-btn-sm np-btn-ghost p-0" style={{ fontSize: 12 }} onClick={() => setConfirmDeleteAction(null)}>No</button>
                      </>
                    ) : (
                      <button className="np-icon-btn np-icon-btn-danger" title="Delete" onClick={() => setConfirmDeleteAction(a.id)}>
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    )}
                  </span>
                </div>
              )
            )}
            <div className="row g-2 mt-3">
              <div className="col-md-4"><input className="np-input" placeholder="New action…" value={newAction.description} onChange={(e) => setNewAction({ ...newAction, description: e.target.value })} /></div>
              <div className="col-md-3">
                <Select
                  options={people.map((p: any) => ({ value: p.personId, label: p.personName }))}
                  value={(() => {
                    const opt = people.find((p: any) => p.personId === newAction.ownerId);
                    return opt ? { value: opt.personId, label: opt.personName } : null;
                  })()}
                  onChange={(opt) => setNewAction({ ...newAction, ownerId: opt?.value ?? '' })}
                  placeholder="— owner —"
                  isClearable
                  isSearchable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={reactSelectStyles()}
                />
              </div>
              <div className="col-md-3"><input type="date" className="np-input" value={newAction.dueDate} onChange={(e) => setNewAction({ ...newAction, dueDate: e.target.value })} /></div>
              <div className="col-md-2">
                <button className="np-btn np-btn-sm np-btn-primary w-100" disabled={!newAction.description.trim()} onClick={() => addActionMutation.mutate()}>Add</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'Actions' && rolledUpActions.length > 0 && (
        <div className="np-card mt-3">
          <div className="np-card-head"><h2 className="mb-0">From this programme's projects</h2></div>
          <div className="np-card-body pt-2">
            {rolledUpActions.map(({ action, fromInitiative }) => (
              <Link
                key={action.id}
                to={`/tracker/initiatives/${fromInitiative.initiativeId}`}
                className="d-flex align-items-center justify-content-between py-2 border-bottom"
                style={{ color: 'inherit' }}
              >
                <span>
                  {action.description}
                  <span className="np-sub ms-2" style={{ fontSize: 12 }}>{fromInitiative.name}</span>
                </span>
                <span className={`np-chip np-tone-${action.status === 'Done' ? 'success' : 'warning'}`}>{action.status}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {tab === 'Actions' && (
        <div className="np-card mt-3">
          <div className="np-card-head"><h2 className="mb-0">From meeting minutes</h2></div>
          <div className="np-card-body">
            {minutesLoading ? (
              <div className="text-center np-sub py-4">Loading…</div>
            ) : openActions.length === 0 ? (
              <div className="text-center np-sub py-4">
                <i className="ri-check-double-line d-block mb-2" style={{ fontSize: '1.75rem' }}></i>
                No open actions from meetings pinned to this initiative{initiative.tier === 'Program' ? ' or its projects' : ''}.
              </div>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="np-sub small">
                    {openActions.length} open action{openActions.length === 1 ? '' : 's'}
                  </span>
                </div>
                <NimbusTable
                  thead={
                    <tr>
                      <th>Action</th>
                      <th style={{ width: '160px' }}>Owner</th>
                      <th style={{ width: '150px' }}>Due</th>
                      <th style={{ width: '200px' }}>Meeting</th>
                      {initiative.tier === 'Program' && <th style={{ width: '140px' }}>Project</th>}
                      <th style={{ width: '110px' }}></th>
                    </tr>
                  }
                >
                  {openActions.map(({ source, entry, matchedInitiativeId }) => {
                    const overdue = !!entry.dueDate && entry.dueDate < todayIso();
                    const busy =
                      closeActionMutation.isPending &&
                      closeActionMutation.variables?.entry.entryId === entry.entryId;
                    const sourceName = rollupSourceName(matchedInitiativeId);
                    return (
                      <tr
                        key={`${source.minutesId}-${entry.entryId}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/meeting-minutes/${source.minutesId}`)}
                      >
                        <td>
                          {entryPlainText(entry.body) || <span className="np-sub fst-italic">No detail recorded</span>}
                        </td>
                        <td>{entry.ownerId ? personName(entry.ownerId) : <span className="np-sub">Unassigned</span>}</td>
                        <td>
                          {entry.dueDate ? (
                            overdue
                              ? <span className="np-chip np-tone-danger">Overdue — {formatDate(entry.dueDate)}</span>
                              : <span className="np-chip np-tone-info">{formatDate(entry.dueDate)}</span>
                          ) : (
                            <span className="np-sub">—</span>
                          )}
                        </td>
                        <td className="np-sub">
                          {source.title || 'Untitled'} · {formatDate(source.meetingDate)}
                        </td>
                        {initiative.tier === 'Program' && (
                          <td className="np-sub">{sourceName || <span className="fst-italic">This programme</span>}</td>
                        )}
                        <td onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="np-btn np-btn-sm np-btn-ghost-success"
                            disabled={busy}
                            onClick={() => closeActionMutation.mutate({ source, entry })}
                          >
                            <i className="ri-check-line"></i> Mark done
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </NimbusTable>
              </>
            )}
          </div>
        </div>
      )}

      {tab === 'Decisions' && (
        <div className="np-card">
          <div className="np-card-body">
            {(initiative.decisions || []).length === 0 && (
              <div className="text-center np-sub py-4">
                <i className="ri-discuss-line d-block mb-2" style={{ fontSize: '1.75rem' }}></i>
                No decisions recorded directly on this initiative.
              </div>
            )}
            {(initiative.decisions || []).map((d: Decision) =>
              editingDecision === d.id && decisionForm ? (
                <div key={d.id} className="d-flex align-items-center gap-2 py-2 border-bottom flex-wrap">
                  <input
                    className="np-input flex-grow-1"
                    style={{ minWidth: 180 }}
                    value={decisionForm.description}
                    autoFocus
                    onChange={(e) => setDecisionForm({ ...decisionForm, description: e.target.value })}
                  />
                  <select
                    className="np-select"
                    style={{ width: 170 }}
                    value={decisionForm.decidedBy || ''}
                    onChange={(e) => setDecisionForm({ ...decisionForm, decidedBy: e.target.value })}
                  >
                    <option value="">— decided by —</option>
                    {people.map((p: any) => <option key={p.personId} value={p.personId}>{p.personName}</option>)}
                  </select>
                  <button
                    className="np-btn np-btn-sm np-btn-primary"
                    disabled={!decisionForm.description.trim() || updateDecisionMutation.isPending}
                    onClick={() => updateDecisionMutation.mutate(decisionForm)}
                  >
                    Save
                  </button>
                  <button className="np-btn np-btn-sm np-btn-ghost" onClick={() => { setEditingDecision(null); setDecisionForm(null); }}>Cancel</button>
                </div>
              ) : (
                <div key={d.id} className="d-flex align-items-center justify-content-between py-2 border-bottom flex-wrap gap-2">
                  <span>
                    {d.description}
                    {d.decidedBy && <span className="np-sub ms-2" style={{ fontSize: 12 }}>{personName(d.decidedBy)}</span>}
                  </span>
                  <span className="d-flex align-items-center gap-2">
                    <button className="np-icon-btn" title="Edit" onClick={() => { setEditingDecision(d.id); setDecisionForm(d); }}>
                      <i className="ri-edit-line"></i>
                    </button>
                    {confirmDeleteDecision === d.id ? (
                      <>
                        <span className="text-danger" style={{ fontSize: 12 }}>Delete?</span>
                        <button className="np-btn np-btn-sm np-btn-ghost text-danger p-0" style={{ fontSize: 12 }} disabled={deleteDecisionMutation.isPending} onClick={() => deleteDecisionMutation.mutate(d.id)}>Yes</button>
                        <button className="np-btn np-btn-sm np-btn-ghost p-0" style={{ fontSize: 12 }} onClick={() => setConfirmDeleteDecision(null)}>No</button>
                      </>
                    ) : (
                      <button className="np-icon-btn np-icon-btn-danger" title="Delete" onClick={() => setConfirmDeleteDecision(d.id)}>
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    )}
                  </span>
                </div>
              )
            )}
            <div className="row g-2 mt-3">
              <div className="col-8"><input className="np-input" placeholder="New decision…" value={newDecision.description} onChange={(e) => setNewDecision({ ...newDecision, description: e.target.value })} /></div>
              <div className="col-2">
                <select className="np-select" value={newDecision.decidedBy} onChange={(e) => setNewDecision({ ...newDecision, decidedBy: e.target.value })}>
                  <option value="">— decided by —</option>
                  {people.map((p: any) => <option key={p.personId} value={p.personId}>{p.personName}</option>)}
                </select>
              </div>
              <div className="col-2">
                <button className="np-btn np-btn-sm np-btn-primary w-100" disabled={!newDecision.description.trim()} onClick={() => addDecisionMutation.mutate()}>Add</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'Decisions' && rolledUpDecisions.length > 0 && (
        <div className="np-card mt-3">
          <div className="np-card-head"><h2 className="mb-0">From this programme's projects</h2></div>
          <div className="np-card-body pt-2">
            {rolledUpDecisions.map(({ decision, fromInitiative }) => (
              <Link
                key={decision.id}
                to={`/tracker/initiatives/${fromInitiative.initiativeId}`}
                className="d-flex align-items-center justify-content-between py-2 border-bottom"
                style={{ color: 'inherit' }}
              >
                <span>
                  {decision.description}
                  <span className="np-sub ms-2" style={{ fontSize: 12 }}>{fromInitiative.name}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {tab === 'Decisions' && (
        <div className="np-card mt-3">
          <div className="np-card-head"><h2 className="mb-0">From meeting minutes</h2></div>
          <div className="np-card-body">
            {decisionEntries.length === 0 ? (
              <div className="text-center np-sub py-4">
                <i className="ri-discuss-line d-block mb-2" style={{ fontSize: '1.75rem' }}></i>
                No decisions linked to this initiative{initiative.tier === 'Program' ? ' or its projects' : ''} yet.
              </div>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <span className="np-sub small">
                    {decisionEntries.length} decision{decisionEntries.length === 1 ? '' : 's'}
                  </span>
                </div>
                <NimbusTable
                  thead={
                    <tr>
                      <th>Decision</th>
                      <th style={{ width: '200px' }}>Meeting</th>
                      {initiative.tier === 'Program' && <th style={{ width: '140px' }}>Project</th>}
                    </tr>
                  }
                >
                  {decisionEntries.map(({ source, entry, matchedInitiativeId }) => {
                    const sourceName = rollupSourceName(matchedInitiativeId);
                    return (
                      <tr
                        key={`${source.minutesId}-${entry.entryId}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/meeting-minutes/${source.minutesId}`)}
                      >
                        <td>
                          {entryPlainText(entry.body) || <span className="np-sub fst-italic">No detail recorded</span>}
                        </td>
                        <td className="np-sub">
                          {source.title || 'Untitled'} · {formatDate(source.meetingDate)}
                        </td>
                        {initiative.tier === 'Program' && (
                          <td className="np-sub">{sourceName || <span className="fst-italic">This programme</span>}</td>
                        )}
                      </tr>
                    );
                  })}
                </NimbusTable>
              </>
            )}
          </div>
        </div>
      )}

      {tab === 'Reports' && (initiative.tier === 'Program' || initiative.tier === 'Portfolio') && (
        <>
          <div className="np-grid np-grid-3 mb-3">
            <div className="np-card">
              <div className="np-card-body np-stat">
                <p className="np-stat-label">CI Health</p>
                <p className={`np-stat-value np-tabular${ciHealthScore === null ? '' : ` np-tone-${scoreTone(ciHealthScore)}`}`}>{ciHealthScore === null ? '—' : `${ciHealthScore}%`}</p>
              </div>
            </div>
            <div className="np-card">
              <div className="np-card-body np-stat">
                <p className="np-stat-label">Operational Health</p>
                <p className={`np-stat-value np-tabular${opHealth.score === null ? '' : ` np-tone-${scoreTone(opHealth.score)}`}`}>{opHealth.score === null ? '—' : `${opHealth.score}%`}</p>
              </div>
            </div>
            <div className="np-card">
              <div className="np-card-body np-stat">
                <p className="np-stat-label">Needs attention</p>
                <p className={`np-stat-value np-tabular${(
                  (initiative.risks || []).filter((r) => r.status === 'Open').length +
                  rolledUpRisks.filter((x) => x.risk.status === 'Open').length +
                  (initiative.exceptions || []).filter((e) => e.status === 'Pending').length +
                  rolledUpExceptions.filter((x) => x.exception.status === 'Pending').length
                ) > 0 ? ' np-tone-danger' : ''}`}>
                  {(initiative.risks || []).filter((r) => r.status === 'Open').length +
                    rolledUpRisks.filter((x) => x.risk.status === 'Open').length +
                    (initiative.exceptions || []).filter((e) => e.status === 'Pending').length +
                    rolledUpExceptions.filter((x) => x.exception.status === 'Pending').length}
                </p>
                <p className="np-stat-trend" style={{ color: 'var(--bs-secondary-color)' }}>open risks &amp; pending exceptions</p>
              </div>
            </div>
          </div>

          <div className={`np-banner np-tone-${thisWeekReport ? 'success' : 'warning'} mb-3 d-flex align-items-center flex-wrap`}>
            <i className={thisWeekReport ? 'ri-checkbox-circle-line' : 'ri-time-line'}></i>
            <span className={`np-chip np-tone-${thisWeekReport ? 'success' : 'warning'} me-2`}>
              {thisWeekReport ? 'Filed' : 'Not filed'}
            </span>
            <span>
              {thisWeekReport
                ? <>Week of {formatDate(thisWeekReport.weekOf)}, by {personName(thisWeekReport.submittedBy)} — {fmtRelative(thisWeekReport.createdAt)}.</>
                : <>This week's report hasn't been filed yet — use "Submit this week's report" above.</>}
            </span>
          </div>

          <div className="np-card mb-3">
            <div className="np-card-head"><h2 className="mb-0">{initiative.tier === 'Portfolio' ? 'Programmes' : 'Projects'} this week</h2></div>
            <div className="np-card-body pt-2">
              {childInitiatives.length === 0 && <p className="np-sub small mb-0">No child {initiative.tier === 'Portfolio' ? 'programmes' : 'projects'} yet.</p>}
              {childInitiatives.map((c: any, ci: number) => {
                const cr = childReports[c.initiativeId];
                const h = effectiveHealth(c);
                const isExpanded = expandedChildId === c.initiativeId;
                const isLastChild = ci === childInitiatives.length - 1;
                // This row's own id plus every descendant of it - for Portfolio tier the census
                // lives at Project depth, two hops below a Programme row, not on the Programme
                // itself (see censusSourceInitiatives above).
                const rowSubtreeIds = new Set<string>([c.initiativeId, ...allInitiatives.filter((i) => i.parentInitiativeId === c.initiativeId).map((i) => i.initiativeId)]);
                const pulse = censusCategoriesForSubtree(reportCensus, rowSubtreeIds);
                return (
                  <div key={c.initiativeId}>
                    <div className="py-2 border-bottom">
                      <div className="d-flex align-items-center">
                        <span className={`np-tree-guide np-tree-guide-corner${isLastChild ? ' np-tree-guide-corner-last' : ''}`}></span>
                        {initiative.tier === 'Portfolio' && (
                          <button
                            type="button"
                            className="np-btn np-btn-sm np-btn-ghost p-0 flex-shrink-0 me-1"
                            style={{ width: 20 }}
                            onClick={() => setExpandedChildId(isExpanded ? null : c.initiativeId)}
                            title={isExpanded ? 'Collapse' : `Show ${c.name}'s own projects`}
                          >
                            <i className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line`}></i>
                          </button>
                        )}
                        <div className={`np-tier-badge np-tier-badge-${tierSlug(c.tier)} flex-shrink-0 me-2`} title={c.tier || 'No tier'}>
                          <i className={c.tier ? TIER_ICON[c.tier] : 'ri-checkbox-blank-circle-line'}></i>
                        </div>
                        <Link to={`/tracker/initiatives/${c.initiativeId}`} className="fw-semibold text-truncate flex-grow-1">{c.name}</Link>
                        <span className="d-flex align-items-center gap-2 flex-shrink-0">
                          {(pulse.milestone || pulse.requirement) && (
                            <span className="d-flex align-items-center gap-1" title={[pulse.milestone && 'milestone change', pulse.requirement && 'requirement change'].filter(Boolean).join(', ')}>
                              {pulse.milestone && <span className="np-pulse-dot np-tone-success"></span>}
                              {pulse.requirement && <span className="np-pulse-dot np-tone-info"></span>}
                            </span>
                          )}
                          <span className={`np-chip np-tone-${HEALTH_COLOR[h]}`}>{h}</span>
                          {cr?.latest ? (
                            <span className="np-tabular small">
                              {cr.latest.percentComplete}%
                              {cr.delta !== null && <span className={cr.delta >= 0 ? 'np-tone-success' : 'np-tone-danger'}> ({cr.delta >= 0 ? '+' : ''}{cr.delta}%)</span>}
                            </span>
                          ) : (
                            <span className="np-chip np-tone-neutral">No reports yet</span>
                          )}
                        </span>
                      </div>
                      <p className="np-sub small mb-0 text-truncate" style={{ marginLeft: '2.7rem' }}>
                        {cr?.latest ? stripHtml(cr.latest.nextSteps) || stripHtml(cr.latest.blockers) || stripHtml(cr.latest.summary) || 'No detail recorded.' : 'No reports yet.'}
                      </p>
                    </div>
                    {isExpanded && (
                      <div className="border-bottom pb-2">
                        {expandedGrandchildren.length === 0 && <p className="np-sub small mb-0 mt-2" style={{ marginLeft: '2.7rem' }}>No projects underneath yet.</p>}
                        {expandedGrandchildren.map((gc, gi) => {
                          const gcReport = grandchildLatestReport[gc.initiativeId];
                          const gh = effectiveHealth(gc);
                          const isLastGrandchild = gi === expandedGrandchildren.length - 1;
                          return (
                            <div key={gc.initiativeId} className="d-flex align-items-center py-1">
                              <span className={`np-tree-guide${isLastChild ? '' : ' np-tree-guide-line'}`}></span>
                              <span className={`np-tree-guide np-tree-guide-corner${isLastGrandchild ? ' np-tree-guide-corner-last' : ''}`}></span>
                              <div className={`np-tier-badge np-tier-badge-${tierSlug(gc.tier)} flex-shrink-0 me-2`} title={gc.tier || 'No tier'}>
                                <i className={gc.tier ? TIER_ICON[gc.tier] : 'ri-checkbox-blank-circle-line'}></i>
                              </div>
                              <Link to={`/tracker/initiatives/${gc.initiativeId}`} className="small text-truncate flex-grow-1">{gc.name}</Link>
                              <span className={`np-chip np-tone-${HEALTH_COLOR[gh]}`}>{gh}</span>
                              {gcReport && <span className="np-tabular small ms-2">{gcReport.percentComplete}%</span>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="np-card mb-3">
            <div className="np-card-head"><h2 className="mb-0">What changed this week</h2></div>
            <div className="np-card-body pt-2">
              <div className="np-banner np-tone-primary np-census-strip mb-3">
                <span className="np-census-total">{reportCensus.totalChanges}</span>
                <span className="np-sub small">change{reportCensus.totalChanges === 1 ? '' : 's'} across {new Set([...reportCensus.milestoneChanges, ...reportCensus.requirementChanges].map((c) => c.childId)).size} projects</span>
                <span className={`np-chip np-tone-success ${reportCensus.milestoneChanges.length === 0 ? 'np-chip-muted' : ''}`}>
                  <span className="np-pulse-dot np-tone-success"></span>{reportCensus.milestoneChanges.length} milestone{reportCensus.milestoneChanges.length === 1 ? '' : 's'}
                </span>
                <span className={`np-chip np-tone-info ${reportCensus.requirementChanges.length === 0 ? 'np-chip-muted' : ''}`}>
                  <span className="np-pulse-dot np-tone-info"></span>{reportCensus.requirementChanges.length} requirement{reportCensus.requirementChanges.length === 1 ? '' : 's'} met
                </span>
              </div>
              {reportCensus.totalChanges === 0 && (
                <p className="np-sub small mb-0">Nothing has changed below since last report.</p>
              )}
              {reportCensus.milestoneChanges.length > 0 && (
                <>
                  <p className="np-stat-label mb-2">Milestones ({reportCensus.milestoneChanges.length})</p>
                  {(showAllMilestoneChanges ? reportCensus.milestoneChanges : reportCensus.milestoneChanges.slice(0, CENSUS_ROW_CAP)).map((c) => (
                    <div key={`${c.childId}-${c.milestoneId}`} className="d-flex align-items-center gap-2 py-1 border-bottom">
                      {c.late && <span className="np-chip np-tone-danger flex-shrink-0">Overdue</span>}
                      <span className="text-truncate flex-grow-1" style={{ minWidth: 0 }} title={c.name}>{c.name}</span>
                      <Link to={`/tracker/initiatives/${c.childId}`} className="np-chip np-tone-neutral flex-shrink-0 text-decoration-none">{c.childName}</Link>
                      {!c.late && <span className="np-sub small np-tabular flex-shrink-0">{formatMilestoneChange(c)}</span>}
                    </div>
                  ))}
                  {reportCensus.milestoneChanges.length > CENSUS_ROW_CAP && (
                    <button type="button" className="np-btn np-btn-sm np-btn-ghost p-0 mt-1" onClick={() => setShowAllMilestoneChanges((v) => !v)}>
                      <i className={`ri-arrow-${showAllMilestoneChanges ? 'up' : 'down'}-s-line`}></i>
                      {showAllMilestoneChanges ? 'Show fewer' : `${reportCensus.milestoneChanges.length - CENSUS_ROW_CAP} more`}
                    </button>
                  )}
                </>
              )}
              {reportCensus.requirementChanges.length > 0 && (
                <>
                  <p className="np-stat-label mb-2 mt-3">Requirements ({reportCensus.requirementChanges.length})</p>
                  {(showAllRequirementChanges ? reportCensus.requirementChanges : reportCensus.requirementChanges.slice(0, CENSUS_ROW_CAP)).map((c) => (
                    <div key={`${c.childId}-${c.requirementId}`} className="d-flex align-items-center gap-2 py-1 border-bottom">
                      <span className="text-truncate flex-grow-1" style={{ minWidth: 0 }} title={c.text}>{c.text}</span>
                      <Link to={`/tracker/initiatives/${c.childId}`} className="np-chip np-tone-neutral flex-shrink-0 text-decoration-none">{c.childName}</Link>
                      <span className="np-sub small flex-shrink-0">{c.from} → {c.to}</span>
                    </div>
                  ))}
                  {reportCensus.requirementChanges.length > CENSUS_ROW_CAP && (
                    <button type="button" className="np-btn np-btn-sm np-btn-ghost p-0 mt-1" onClick={() => setShowAllRequirementChanges((v) => !v)}>
                      <i className={`ri-arrow-${showAllRequirementChanges ? 'up' : 'down'}-s-line`}></i>
                      {showAllRequirementChanges ? 'Show fewer' : `${reportCensus.requirementChanges.length - CENSUS_ROW_CAP} more`}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="np-card">
            <div className="np-card-head"><h2 className="mb-0">Metrics dashboard</h2></div>
            <div className="np-card-body pt-2">
              {rollupMetrics.length === 0 ? (
                <p className="np-sub small mb-0">No metrics linked to this initiative or anything underneath it yet.</p>
              ) : (
                <div className="row g-3">
                  {rollupMetrics.map((m: any) => {
                    const metricReadings = readingsByMetric[m.metricId] || [];
                    const status = computeMetricStatus(m, metricReadings);
                    const latest = metricReadings[metricReadings.length - 1]?.value;
                    const color = `var(--bs-${status === 'on-target' ? 'success' : status === 'off-target' ? 'danger' : 'secondary'})`;
                    return (
                      <div key={m.metricId} className="col-md-6 col-xl-4">
                        <div className="d-flex align-items-center gap-2">
                          {metricReadings.length > 0 && metricReadings.length < 3 ? (
                            <MetricRing value={latest} target={m.target} direction={m.direction} color={color} />
                          ) : (
                            <Sparkline values={metricReadings.map((r: any) => r.value)} color={color} />
                          )}
                          <div style={{ minWidth: 0 }}>
                            <p className="mb-0 small fw-semibold text-truncate">{m.name}</p>
                            <span className={`np-chip np-tone-${status === 'on-target' ? 'success' : status === 'off-target' ? 'danger' : 'neutral'}`}>{status.replace('-', ' ')}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

        </>
      )}

      {tab === 'Reports' && (
        <div className="np-card">
          <div className="np-card-body">
            {reports.length === 0 && (
              <div className="text-center np-sub py-4">
                <i className="ri-file-list-3-line d-block mb-2" style={{ fontSize: '1.75rem' }}></i>
                No reports submitted yet.
              </div>
            )}
            {reports.length > 0 && (
              <div className="d-flex flex-wrap gap-1 mb-3 pb-3 border-bottom">
                {[...reports].sort((a, b) => a.weekOf.localeCompare(b.weekOf)).map((r) => (
                  <button
                    key={r.reportId}
                    type="button"
                    className={`np-chip np-tone-${r.weekOf === currentWeekOf ? 'primary' : HEALTH_COLOR[r.health] || 'neutral'}`}
                    style={{ fontFamily: 'var(--bs-font-monospace, monospace)' }}
                    onClick={() => navigate(`/tracker/initiatives/${id}/report/${r.reportId}`)}
                    title={`${r.health} · ${r.percentComplete}%`}
                  >
                    {formatDate(r.weekOf)}
                  </button>
                ))}
                {!thisWeekReport && (
                  <span className="np-chip np-tone-neutral" style={{ opacity: 0.6, fontFamily: 'var(--bs-font-monospace, monospace)' }} title="Not yet filed">
                    {formatDate(currentWeekOf)} — due
                  </span>
                )}
              </div>
            )}
            {reports.map((r, i) => (
              <div key={r.reportId} className="np-timeline-item" style={{ borderLeftColor: i === reports.length - 1 ? 'transparent' : undefined }}>
                <span className="np-timeline-dot" style={{ background: r.health === 'On Track' ? 'var(--bs-success)' : r.health === 'At Risk' ? 'var(--bs-warning)' : 'var(--bs-danger)' }}></span>
                <div className="d-flex align-items-center justify-content-between">
                  <p className="np-timeline-title mb-0" style={{ color: r.health === 'On Track' ? 'var(--bs-success)' : r.health === 'At Risk' ? 'var(--bs-warning)' : 'var(--bs-danger)' }}>Week of {formatDate(r.weekOf)}</p>
                  <span className="d-flex align-items-center gap-2">
                    <button className="np-btn np-btn-sm np-btn-ghost p-0" style={{ fontSize: 12 }} onClick={() => navigate(`/tracker/initiatives/${id}/report/${r.reportId}`)}>
                      <i className="ri-edit-line me-1"></i>Edit
                    </button>
                    {confirmDeleteReport === r.reportId ? (
                      <>
                        <span className="text-danger" style={{ fontSize: 12 }}>Delete?</span>
                        <button className="np-btn np-btn-sm np-btn-ghost text-danger p-0" style={{ fontSize: 12 }} disabled={deleteReportMutation.isPending} onClick={() => deleteReportMutation.mutate(r.reportId)}>Yes</button>
                        <button className="np-btn np-btn-sm np-btn-ghost p-0" style={{ fontSize: 12 }} onClick={() => setConfirmDeleteReport(null)}>No</button>
                      </>
                    ) : (
                      <button className="np-btn np-btn-sm np-btn-ghost text-danger p-0" style={{ fontSize: 12 }} onClick={() => setConfirmDeleteReport(r.reportId)}>
                        <i className="ri-delete-bin-line me-1"></i>Delete
                      </button>
                    )}
                  </span>
                </div>
                <p className="np-timeline-meta">{personName(r.submittedBy)} · {r.health} · {r.percentComplete}% · {fmtRelative(r.createdAt)}</p>
                {(r.milestoneUpdates || []).length > 0 && (
                  <div className="d-flex flex-wrap gap-1 mb-2">
                    {r.milestoneUpdates.map((u) => (
                      <span key={u.milestoneId} className={`np-chip np-tone-${toneOf(u.status === 'Done' ? 'success' : u.status === 'Missed' ? 'danger' : u.status === 'At Risk' ? 'warning' : 'secondary')}`}>
                        {milestoneName(u.milestoneId)}: {u.status} ({u.completionPercent}%)
                      </span>
                    ))}
                  </div>
                )}
                <RichTextView className="mb-1" value={r.summary} empty="" />
                {!isRichTextEmpty(r.blockers) && (
                  <div className="mb-1 np-sub small"><strong>Blockers:</strong> <RichTextView value={r.blockers} empty="" /></div>
                )}
                {!isRichTextEmpty(r.nextSteps) && (
                  <div className="mb-0 np-sub small"><strong>Next:</strong> <RichTextView value={r.nextSteps} empty="" /></div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'Risks & Blockers' && (
        <div className="np-card">
          <div className="np-card-body">
            {(initiative.risks || []).length === 0 && (
              <div className="text-center np-sub py-4">
                <i className="ri-shield-check-line d-block mb-2" style={{ fontSize: '1.75rem' }}></i>
                No risks or blockers recorded.
              </div>
            )}
            {(initiative.risks || []).map((r: Risk) =>
              editingRisk === r.id && riskForm ? (
                <div key={r.id} className="d-flex align-items-center gap-2 py-2 border-bottom">
                  <input
                    className="np-input flex-grow-1"
                    value={riskForm.description}
                    autoFocus
                    onChange={(e) => setRiskForm({ ...riskForm, description: e.target.value })}
                  />
                  <select
                    className="np-select"
                    style={{ width: 110 }}
                    value={riskForm.severity}
                    onChange={(e) => setRiskForm({ ...riskForm, severity: e.target.value as Risk['severity'] })}
                  >
                    {['Low', 'Medium', 'High', 'Critical'].map((s) => <option key={s}>{s}</option>)}
                  </select>
                  <select
                    className="np-select"
                    style={{ width: 110 }}
                    value={riskForm.status}
                    onChange={(e) => setRiskForm({ ...riskForm, status: e.target.value as Risk['status'] })}
                  >
                    {['Open', 'Mitigated', 'Closed'].map((s) => <option key={s}>{s}</option>)}
                  </select>
                  <button
                    className="np-btn np-btn-sm np-btn-primary"
                    disabled={!riskForm.description.trim() || updateRiskMutation.isPending}
                    onClick={() => updateRiskMutation.mutate(riskForm)}
                  >
                    Save
                  </button>
                  <button className="np-btn np-btn-sm np-btn-ghost" onClick={() => { setEditingRisk(null); setRiskForm(null); }}>Cancel</button>
                </div>
              ) : (
                <div key={r.id} className="d-flex align-items-center justify-content-between py-2 border-bottom">
                  <span>{r.description}</span>
                  <span className="d-flex align-items-center gap-2">
                    <span className={`np-chip np-tone-${toneOf(r.severity === 'Critical' || r.severity === 'High' ? 'danger' : r.severity === 'Medium' ? 'warning' : 'secondary')}`}>{r.severity}</span>
                    <span className={`np-chip np-tone-${toneOf(r.status === 'Open' ? 'danger' : r.status === 'Mitigated' ? 'warning' : 'success')}`}>{r.status}</span>
                    <button className="np-icon-btn" title="Edit" onClick={() => { setEditingRisk(r.id); setRiskForm(r); }}>
                      <i className="ri-edit-line"></i>
                    </button>
                    {confirmDeleteRisk === r.id ? (
                      <>
                        <span className="text-danger" style={{ fontSize: 12 }}>Delete?</span>
                        <button className="np-btn np-btn-sm np-btn-ghost text-danger p-0" style={{ fontSize: 12 }} disabled={deleteRiskMutation.isPending} onClick={() => deleteRiskMutation.mutate(r.id)}>Yes</button>
                        <button className="np-btn np-btn-sm np-btn-ghost p-0" style={{ fontSize: 12 }} onClick={() => setConfirmDeleteRisk(null)}>No</button>
                      </>
                    ) : (
                      <button className="np-icon-btn np-icon-btn-danger" title="Delete" onClick={() => setConfirmDeleteRisk(r.id)}>
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    )}
                  </span>
                </div>
              )
            )}
            <div className="row g-2 mt-3">
              <div className="col-6"><input className="np-input" placeholder="New risk / blocker…" value={newRisk.description} onChange={(e) => setNewRisk({ ...newRisk, description: e.target.value })} /></div>
              <div className="col-3">
                <select className="np-select" value={newRisk.severity} onChange={(e) => setNewRisk({ ...newRisk, severity: e.target.value })}>
                  {['Low', 'Medium', 'High', 'Critical'].map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="col-3">
                <button className="np-btn np-btn-sm np-btn-primary w-100" disabled={!newRisk.description.trim()} onClick={() => addRiskMutation.mutate()}>Add</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'Risks & Blockers' && rolledUpRisks.length > 0 && (
        <div className="np-card mt-3">
          <div className="np-card-head"><h2 className="mb-0">From this programme's projects</h2></div>
          <div className="np-card-body pt-2">
            {rolledUpRisks.map(({ risk, fromInitiative }) => (
              <Link
                key={risk.id}
                to={`/tracker/initiatives/${fromInitiative.initiativeId}`}
                className="d-flex align-items-center justify-content-between py-2 border-bottom"
                style={{ color: 'inherit' }}
              >
                <span>
                  {risk.description}
                  <span className="np-sub ms-2" style={{ fontSize: 12 }}>{fromInitiative.name}</span>
                </span>
                <span className="d-flex align-items-center gap-2">
                  <span className={`np-chip np-tone-${toneOf(risk.severity === 'Critical' || risk.severity === 'High' ? 'danger' : risk.severity === 'Medium' ? 'warning' : 'secondary')}`}>{risk.severity}</span>
                  <span className={`np-chip np-tone-${toneOf(risk.status === 'Open' ? 'danger' : risk.status === 'Mitigated' ? 'warning' : 'success')}`}>{risk.status}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {tab === 'Risks & Blockers' && riskBlockerEntries.length > 0 && (
        <div className="np-card mt-3">
          <div className="np-card-head"><h2 className="mb-0">From meeting minutes</h2></div>
          <div className="np-card-body">
            <NimbusTable
              thead={
                <tr>
                  <th style={{ width: '110px' }}>Type</th>
                  <th>Detail</th>
                  <th style={{ width: '200px' }}>Meeting</th>
                  {initiative.tier === 'Program' && <th style={{ width: '140px' }}>Project</th>}
                </tr>
              }
            >
              {riskBlockerEntries.map(({ source, entry, matchedInitiativeId }) => {
                const sourceName = rollupSourceName(matchedInitiativeId);
                return (
                  <tr
                    key={`${source.minutesId}-${entry.entryId}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/meeting-minutes/${source.minutesId}`)}
                  >
                    <td>
                      <span className={`np-chip np-tone-${toneOf(entry.category === 'risk' ? 'danger' : 'warning')}`}>
                        {entry.category === 'risk' ? 'Risk' : 'Blocker'}
                      </span>
                    </td>
                    <td>
                      {entryPlainText(entry.body) || <span className="np-sub fst-italic">No detail recorded</span>}
                    </td>
                    <td className="np-sub">
                      {source.title || 'Untitled'} · {formatDate(source.meetingDate)}
                    </td>
                    {initiative.tier === 'Program' && (
                      <td className="np-sub">{sourceName || <span className="fst-italic">This programme</span>}</td>
                    )}
                  </tr>
                );
              })}
            </NimbusTable>
          </div>
        </div>
      )}

      {tab === 'Metrics' && (
        // Metrics are created here now, not on the Catalog page - a Project's metric proves
        // it delivered value; "Send to Catalog" hands it to ongoing tracking, the same
        // owner+cadence step Mark Completed (Overview tab) already asks for, just available
        // any time. A Program/Portfolio's own metrics are tracked from the moment they're
        // created - it's already ongoing by nature, nothing to send.
        <div className={`np-card ${targetMetrics.length === 0 ? 'np-card-accent-warning' : 'np-card-accent-primary'}`}>
          <div className="np-card-head">
            <h2 className="mb-0">Metrics</h2>
            {!showNewMetricForm && (
              <button type="button" className="np-btn np-btn-primary np-btn-sm ms-auto" onClick={() => setShowNewMetricForm(true)}>
                <i className="ri-add-line"></i>New Metric
              </button>
            )}
          </div>
          <div className="np-card-body">
            {targetMetrics.length === 0 && !showNewMetricForm && (
              <div className="text-center np-sub py-3">
                <i className="ri-line-chart-line d-block mb-2" style={{ fontSize: '1.5rem' }}></i>
                No metrics yet — the dependent variable(s) this should move.
              </div>
            )}
            {targetMetrics.map((m: any) => {
              const readings = readingsByMetric[m.metricId] || [];
              const latest = readings[readings.length - 1]?.value;
              const status = computeMetricStatus(m, readings);
              return (
                <div key={m.metricId} className="py-2" style={{ borderBottom: '1px solid var(--bs-border-color)' }}>
                  <div className="d-flex align-items-center gap-2">
                    <Link to={`/tracker/catalog/metrics/${m.metricId}`} className="flex-grow-1" style={{ minWidth: 0, color: 'inherit', textDecoration: 'none' }}>
                      <div className="text-truncate fw-semibold" style={{ fontSize: 13, color: 'var(--bs-heading-color)' }}>{m.name}</div>
                      <div className="np-sub" style={{ fontSize: 11 }}>
                        {latest != null ? `${latest} ${m.unit}` : 'No readings'}{m.target != null ? ` of ${m.target} target` : ''}
                      </div>
                    </Link>
                    <span className={`np-chip np-tone-${SEVERITY_TONE[(m.criticality || 'Medium') as keyof typeof SEVERITY_TONE]}`} style={{ fontSize: '0.62rem' }}>
                      {m.criticality || 'Medium'}
                    </span>
                    {(status === 'on-target' || status === 'off-target') && (
                      <span className={`np-chip np-tone-${status === 'on-target' ? 'success' : 'danger'}`} style={{ fontSize: '0.62rem' }}>
                        {status === 'on-target' ? 'On target' : 'Off target'}
                      </span>
                    )}
                    {m.trackedAsBau ? (
                      <span className="d-flex align-items-center gap-1" style={{ fontSize: '0.72rem', color: 'var(--bs-success-text-emphasis)' }}>
                        <i className="ri-checkbox-circle-line"></i>In Catalog
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="np-btn np-btn-ghost np-btn-sm"
                        onClick={() => { setSendingToCatalog(m.metricId); setSendForm({ bauOwnerId: initiative.ownerId, cadence: m.cadence || 'Monthly' }); }}
                      >
                        <i className="ri-send-plane-line"></i>{parentInitiative ? 'Send to Program & Catalog' : 'Send to Catalog'}
                      </button>
                    )}
                  </div>
                  {sendingToCatalog === m.metricId && (
                    <div className="mt-2 p-2 rounded" style={{ background: 'var(--bs-primary-bg-subtle)' }}>
                      {parentInitiative && (
                        <p className="np-sub mb-2" style={{ fontSize: '0.74rem' }}>
                          <i className="ri-corner-left-up-line me-1"></i>
                          Also links this metric into <strong>{parentInitiative.name}</strong>'s own Metrics tab, so it can take over monitoring it.
                        </p>
                      )}
                      <div className="row g-2 mb-2">
                        <div className="col-6">
                          <label className="np-field-caption">Owner going forward</label>
                          <select className="np-select" value={sendForm.bauOwnerId} onChange={(e) => setSendForm({ ...sendForm, bauOwnerId: e.target.value })}>
                            {people.map((p: any) => <option key={p.personId} value={p.personId}>{p.personName}</option>)}
                          </select>
                        </div>
                        <div className="col-6">
                          <label className="np-field-caption">Reading cadence</label>
                          <select className="np-select" value={sendForm.cadence} onChange={(e) => setSendForm({ ...sendForm, cadence: e.target.value })}>
                            {['Weekly', 'Monthly', 'Quarterly', 'Bi-annually', 'Annually'].map((c) => <option key={c}>{c}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="np-btn np-btn-primary np-btn-sm"
                          disabled={sendToCatalogMutation.isPending}
                          onClick={() => sendToCatalogMutation.mutate({ metricId: m.metricId, ...sendForm })}
                        >
                          <i className="ri-check-line"></i>Confirm & Send
                        </button>
                        <button type="button" className="np-btn np-btn-ghost np-btn-sm" onClick={() => setSendingToCatalog(null)}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {showNewMetricForm && (
              <div className="mt-2 p-2 rounded" style={{ background: 'var(--bs-secondary-bg-subtle)', border: '1px dashed var(--bs-border-color)' }}>
                <div className="mb-2">
                  <label className="np-field-caption">Name</label>
                  <input className="np-input" placeholder="e.g. Patch SLA compliance" value={newMetricForm.name} onChange={(e) => setNewMetricForm({ ...newMetricForm, name: e.target.value })} />
                </div>
                <div className="row g-2 mb-2">
                  <div className="col-4">
                    <label className="np-field-caption">Unit</label>
                    <input className="np-input" placeholder="%" value={newMetricForm.unit} onChange={(e) => setNewMetricForm({ ...newMetricForm, unit: e.target.value })} />
                  </div>
                  <div className="col-4">
                    <label className="np-field-caption">Direction</label>
                    <select className="np-select" value={newMetricForm.direction} onChange={(e) => setNewMetricForm({ ...newMetricForm, direction: e.target.value })}>
                      <option value="higher">Higher better</option>
                      <option value="lower">Lower better</option>
                    </select>
                  </div>
                  <div className="col-4">
                    <label className="np-field-caption">Cadence</label>
                    <select className="np-select" value={newMetricForm.cadence} onChange={(e) => setNewMetricForm({ ...newMetricForm, cadence: e.target.value })}>
                      {['Weekly', 'Monthly', 'Quarterly', 'Bi-annually', 'Annually'].map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div className="row g-2 mb-2">
                  <div className="col-4">
                    <label className="np-field-caption">Criticality</label>
                    <select className="np-select" value={newMetricForm.criticality} onChange={(e) => setNewMetricForm({ ...newMetricForm, criticality: e.target.value })}>
                      {['Low', 'Medium', 'High', 'Critical'].map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="col-4">
                    <label className="np-field-caption">Baseline</label>
                    <input className="np-input" placeholder="Optional" value={newMetricForm.baseline} onChange={(e) => setNewMetricForm({ ...newMetricForm, baseline: e.target.value })} />
                  </div>
                  <div className="col-4">
                    <label className="np-field-caption">Target</label>
                    <input className="np-input" placeholder="Optional" value={newMetricForm.target} onChange={(e) => setNewMetricForm({ ...newMetricForm, target: e.target.value })} />
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="np-btn np-btn-primary np-btn-sm"
                    disabled={!newMetricForm.name.trim() || createMetricMutation.isPending}
                    onClick={() => createMetricMutation.mutate()}
                  >
                    <i className="ri-check-line"></i>Create Metric
                  </button>
                  <button type="button" className="np-btn np-btn-ghost np-btn-sm" onClick={() => setShowNewMetricForm(false)}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'Timeline' && initiative.tier !== 'Portfolio' && initiative.tier !== 'Program' && (
        <div className="np-card" style={{ padding: '18px 20px' }}>
          {initiative.isBau ? (
            <div className="text-center np-sub py-4">
              <i className="ri-repeat-line d-block mb-2" style={{ fontSize: '1.75rem' }}></i>
              This initiative is marked Standalone Work — it has no start or target end date to plot.
            </div>
          ) : (
          <>
          <Chart
            type="rangeBar"
            height={140}
            series={[{
              data: [
                { x: 'Original plan', y: [new Date(initiative.startDate || initiative.createdAt).getTime(), new Date(initiative.originalTargetEndDate || initiative.createdAt).getTime()] },
                { x: 'Current projection', y: [new Date(initiative.startDate || initiative.createdAt).getTime(), new Date(initiative.currentProjectedEndDate || initiative.originalTargetEndDate || initiative.createdAt).getTime()] },
              ],
            }]}
            options={{
              chart: { toolbar: { show: false }, animations: { enabled: false } },
              plotOptions: { bar: { horizontal: true, distributed: true, barHeight: '45%' } },
              colors: ['#8a8d93', HEALTH_COLOR[effectiveHealth(initiative)] === 'success' ? '#56ca00' : HEALTH_COLOR[effectiveHealth(initiative)] === 'warning' ? '#ffb400' : '#ff4c51'],
              xaxis: { type: 'datetime' },
              legend: { show: false },
              dataLabels: { enabled: false },
            }}
          />
          <p className="np-sub small mt-2 mb-0">
            Slippage: {Math.round((new Date(initiative.currentProjectedEndDate || initiative.originalTargetEndDate).getTime() - new Date(initiative.originalTargetEndDate || initiative.createdAt).getTime()) / 86400000)} days vs. original plan.
          </p>
          </>
          )}
        </div>
      )}

      {tab === 'Progress' && (
        <div className="np-card" style={{ padding: '18px 20px' }}>
          {progressSeries.length === 0 ? (
            <div className="text-center np-sub py-4">
              <i className="ri-line-chart-line d-block mb-2" style={{ fontSize: '1.75rem' }}></i>
              No weekly reports yet — progression shows up here once reporting starts.
            </div>
          ) : (
            <>
              <Chart
                type="line"
                height={280}
                series={[
                  { name: 'Cumulative % complete', type: 'bar', data: progressSeries.map((p) => p.cumulativeComplete) },
                  { name: 'Cumulative % schedule consumed', type: 'line', data: progressSeries.map((p) => p.scheduleConsumed) },
                ]}
                options={{
                  chart: { toolbar: { show: false }, animations: { enabled: false } },
                  plotOptions: { bar: { columnWidth: '45%', borderRadius: 3 } },
                  stroke: { width: [0, 3], curve: 'straight' },
                  markers: { size: 4 },
                  colors: [
                    ({ dataPointIndex }: any) => {
                      const health = progressSeries[dataPointIndex]?.health;
                      if (health === 'On Track') return '#56ca00';
                      if (health === 'At Risk') return '#ffb400';
                      if (health === 'Blocked') return '#ff4c51';
                      return '#8a8d93';
                    },
                    '#16b1ff',
                  ],
                  xaxis: { categories: progressSeries.map((p) => formatDate(p.weekOf)), labels: { style: { colors: 'var(--bs-secondary-color)', fontSize: '11px' } } },
                  yaxis: { max: 100, title: { text: '% of total' }, labels: { formatter: (v: number) => `${Math.round(v)}%` } },
                  grid: { borderColor: 'var(--bs-border-color)' },
                  legend: { show: false },
                  dataLabels: { enabled: false },
                  tooltip: { y: { formatter: (v: number) => `${v}%` } },
                }}
              />
              <div className="np-legend">
                <div className="np-legend-item"><span className="np-legend-swatch" style={{ background: '#56ca00' }}></span>Bar = On Track that week</div>
                <div className="np-legend-item"><span className="np-legend-swatch" style={{ background: '#ffb400' }}></span>At Risk</div>
                <div className="np-legend-item"><span className="np-legend-swatch" style={{ background: '#ff4c51' }}></span>Blocked</div>
                <div className="np-legend-item"><span className="np-legend-swatch" style={{ background: '#16b1ff' }}></span>Line = cumulative schedule consumed</div>
              </div>
              <p className="np-sub small mt-2 mb-0">
                {(() => {
                  const latest = progressSeries[progressSeries.length - 1];
                  const gap = Math.round((latest.scheduleConsumed - latest.cumulativeComplete) * 10) / 10;
                  if (gap > 2) return `Currently at ${latest.cumulativeComplete}% complete vs ${latest.scheduleConsumed}% of the schedule consumed — running ${gap} points behind pace.`;
                  if (gap < -2) return `Currently at ${latest.cumulativeComplete}% complete vs ${latest.scheduleConsumed}% of the schedule consumed — running ${Math.abs(gap)} points ahead of pace.`;
                  return `Currently at ${latest.cumulativeComplete}% complete vs ${latest.scheduleConsumed}% of the schedule consumed — right on pace.`;
                })()}
              </p>
            </>
          )}
        </div>
      )}

      {tab === 'Exceptions' && (
        <div className="np-card">
          <div className="np-card-body">
            {(initiative.exceptions || []).length === 0 && (
              <div className="text-center np-sub py-4">
                <i className="ri-shield-check-line d-block mb-2" style={{ fontSize: '1.75rem' }}></i>
                No projected end date changes have been requested.
              </div>
            )}
            {[...(initiative.exceptions || [])].reverse().map((exc) => (
              <div key={exc.id} className="py-3" style={{ borderBottom: '1px solid var(--bs-border-color)' }}>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div>
                    <span className="fw-semibold">{formatDate(exc.previousProjectedEndDate)} → {formatDate(exc.newProjectedEndDate)}</span>
                    <span className="np-sub ms-2" style={{ fontSize: 12 }}>requested by {personName(exc.requestedBy)} · {fmtRelative(exc.requestedAt)}</span>
                  </div>
                  <span className={`np-chip np-tone-${toneOf(exc.status === 'Approved' ? 'success' : exc.status === 'Rejected' ? 'danger' : 'warning')}`}>{exc.status}</span>
                </div>
                <p className="mb-2 mt-2" style={{ fontSize: 13.5 }}>{exc.reason || <span className="np-sub">No reason given.</span>}</p>
                {exc.status !== 'Pending' && exc.approvedBy && (
                  <p className="np-sub mb-0" style={{ fontSize: 12 }}>
                    {exc.status === 'Approved' ? 'Approved' : 'Rejected'} by {personName(exc.approvedBy)} · {fmtRelative(exc.approvedAt || '')}
                  </p>
                )}
                {exc.status === 'Pending' && (
                  isApprover ? (
                    <div className="d-flex gap-2 mt-2">
                      <button className="np-btn np-btn-sm np-btn-success" disabled={approveExceptionMutation.isPending} onClick={() => approveExceptionMutation.mutate(exc.id)}>
                        <i className="ri-check-line me-1"></i>Approve
                      </button>
                      <button className="np-btn np-btn-sm np-btn-ghost-danger" disabled={rejectExceptionMutation.isPending} onClick={() => rejectExceptionMutation.mutate(exc.id)}>
                        <i className="ri-close-line me-1"></i>Reject
                      </button>
                    </div>
                  ) : (
                    <p className="np-sub mb-0 mt-2" style={{ fontSize: 12 }}>
                      <i className="ri-lock-line me-1"></i>Only designated exception approvers can sign off on this.
                    </p>
                  )
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'Exceptions' && rolledUpExceptions.length > 0 && (
        <div className="np-card mt-3">
          <div className="np-card-head"><h2 className="mb-0">From this programme's projects</h2></div>
          <div className="np-card-body pt-2">
            {rolledUpExceptions.map(({ exception: exc, fromInitiative }) => (
              <Link
                key={exc.id}
                to={`/tracker/initiatives/${fromInitiative.initiativeId}`}
                className="d-block py-3"
                style={{ borderBottom: '1px solid var(--bs-border-color)', color: 'inherit' }}
              >
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div>
                    <span className="fw-semibold">{formatDate(exc.previousProjectedEndDate)} → {formatDate(exc.newProjectedEndDate)}</span>
                    <span className="np-sub ms-2" style={{ fontSize: 12 }}>{fromInitiative.name} · requested by {personName(exc.requestedBy)} · {fmtRelative(exc.requestedAt)}</span>
                  </div>
                  <span className={`np-chip np-tone-${toneOf(exc.status === 'Approved' ? 'success' : exc.status === 'Rejected' ? 'danger' : 'warning')}`}>{exc.status}</span>
                </div>
                <p className="mb-0 mt-2" style={{ fontSize: 13.5 }}>{exc.reason || <span className="np-sub">No reason given.</span>}</p>
                {exc.status === 'Pending' && (
                  <p className="np-sub mb-0 mt-2" style={{ fontSize: 12 }}>
                    <i className="ri-arrow-right-line me-1"></i>Approve or reject on {fromInitiative.name}'s own Exceptions tab.
                  </p>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      {tab === 'Linked Artefacts' && (
        <div className="row">
          <LinkedArtefacts artefactId={id!} artefactType="initiative" artefactRegister="initiatives" />
        </div>
      )}
    </div>
  );
};

const CompleteInitiativePanel: React.FC<{
  initiative: Initiative;
  targetMetrics: any[];
  people: any[];
  onCancel: () => void;
  onConfirm: (handoffs: { metricId: string; bauOwnerId: string; cadence: string }[]) => void;
  pending: boolean;
}> = ({ initiative, targetMetrics, people, onCancel, onConfirm, pending }) => {
  const [handoffs, setHandoffs] = useState(
    targetMetrics.map((m) => ({ metricId: m.metricId, bauOwnerId: initiative.ownerId, cadence: m.cadence || 'Monthly' }))
  );

  return (
    <div className="np-card np-card-accent-success mb-4">
      <div className="np-card-head"><h2 className="mb-0">Complete initiative — hand off to BAU</h2></div>
      <div className="np-card-body">
        {targetMetrics.length === 0 ? (
          <div className="text-center py-5">
            <i className="ri-line-chart-line ri-48px d-block mb-2" style={{ color: 'var(--bs-secondary-color)' }}></i>
            <p className="np-sub mb-0">No target metrics linked to this initiative — it'll simply be marked Completed.</p>
          </div>
        ) : (
          <>
            <p className="np-sub small">For each target metric, confirm who owns it going forward and how often it should be read:</p>
            {targetMetrics.map((m, idx) => (
              <div key={m.metricId} className="row g-2 align-items-center mb-2">
                <div className="col-4" style={{ fontSize: 13, fontWeight: 600 }}>{m.name}</div>
                <div className="col-4">
                  <select
                    className="np-select"
                    value={handoffs[idx].bauOwnerId}
                    onChange={(e) => setHandoffs((prev) => prev.map((h, i) => i === idx ? { ...h, bauOwnerId: e.target.value } : h))}
                  >
                    {people.map((p: any) => <option key={p.personId} value={p.personId}>{p.personName}</option>)}
                  </select>
                </div>
                <div className="col-4">
                  <select
                    className="np-select"
                    value={handoffs[idx].cadence}
                    onChange={(e) => setHandoffs((prev) => prev.map((h, i) => i === idx ? { ...h, cadence: e.target.value } : h))}
                  >
                    {['Weekly', 'Monthly', 'Quarterly', 'Bi-annually', 'Annually'].map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </>
        )}
        <div className="d-flex gap-2 mt-3">
          <button className="np-btn np-btn-success" disabled={pending} onClick={() => onConfirm(handoffs)}>
            {pending ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="ri-check-line me-1"></i>}
            Confirm & Complete
          </button>
          <button className="np-btn np-btn-ghost" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
};
